import { HorizontZeile, SubstratZeile } from '../../data-model';
import { HUMUS_STUFEN } from '../help/humus-stufen';
import { KARBONAT_STUFEN } from '../help/karbonat-stufen';
import { LAGERUNGSDICHTE_STUFEN } from '../help/lagerungsdichte-stufen';
import { SKELETT_STUFEN } from '../help/skelett-stufen';
import { Abschnitt, AuflageStreifen } from './grubenbild-abschnitte';
import { FILZ_MASSBAND_TEXT, angezeigteTiefe } from '../filz-tiefen';
import { wasserstandText } from '../flur-tiefe';
// Umbenannt, weil in dieser Datei schon `WechselLage2` steht - das ist die
// Lage eines Bandes IM Bild, hier geht es um den Platz einer Zeile in ihrer
// Wechselkette.
import { wechselLage, WechselLage as KettenLage } from '../detail/wechsel-gruppen';
import {
	BeschriftungsPlan, SCHRIFT_SPUR, SCHRIFT_WAND, beschriftungsPlan, textBreite,
	wandAusweichenX,
} from './grubenbild-beschriftung';
import { bodenart, bodenartText, SKELETT_ANTEIL } from './grubenbild-bodenarten';
import {
	baenderImAbschnitt, bereichBei, grundmasse, wechselbereiche, WechselLage2,
} from './grubenbild-wechsel';
import {
	HORIZONT_FARBEN, Muster, leseSymbol, mischen, schichtFarbe, tonen,
} from './horizont-farben';
import { legeAuflageAusKacheln } from './auflage-lupe';
import { el, streu } from './svg-werkzeug';

/**
 * Zeichnet das Grubenbild als SVG.
 *
 * Die Wand ist 1,50 m breit und zeigt die GANZE Aufnahmetiefe, maszstabsgerecht
 * in beiden Richtungen. Die Humusauflage steht ueber der Gelaendeoberflaeche.
 *
 * Das Modul haengt an keinem Angular-Dienst; es bekommt die Daten und liefert
 * ein fertiges SVG-Element zurueck.
 */

export const BREITE_CM = 150;
/** So tief ist die Grube; was tiefer erfasst ist, wurde gebohrt. */
export const GRUBE_CM = 200;
const PX_HOECHSTENS = 3.0;
const HOCH_HOECHSTENS = 660;
const RAND_LINKS = 84;
const RAND_UNTEN = 34;
const RAND_OBEN = 64;

/**
 * Maszstab in Pixel je Zentimeter.
 *
 * Er gilt fuer BEIDE Achsen, sonst waere das Bild nicht mehr maszstabsgerecht.
 * Bei einer flachen Aufnahme bleibt es bei 3 px/cm; reicht sie tiefer (oft bis
 * 300 cm), wird der Maszstab kleiner, damit die ganze Aufnahme auf den Schirm
 * passt. Die Bemassung nennt die Tiefe in Metern, die Auskunft stimmt also in
 * jedem Fall.
 */
export function maszstabFuer(gesamtTiefeCm: number): number {
	if (!(gesamtTiefeCm > 0)) return PX_HOECHSTENS;
	return Math.max(1.5, Math.min(PX_HOECHSTENS, HOCH_HOECHSTENS / gesamtTiefeCm));
}

/** „3,00 m" aus 300 cm. */
export function meterText(cm: number): string {
	return (cm / 100).toFixed(2).replace('.', ',') + ' m';
}

export interface GrubenbildDaten {
	abschnitte: Abschnitt[];
	/** `erfasst` unterscheidet „ueberall 0" von „gar nichts eingetragen". */
	auflage: { zeilen: AuflageStreifen[]; gesamt: number; erfasst?: boolean };
	horizonte: HorizontZeile[];
	substrate: SubstratZeile[];
	/**
	 * Der gemessene Wasserstand, beides in cm unter Gelaendeoberflaeche:
	 * `spiegelTiefe` ist der Wasserspiegel, `saugsaum` die Oberkante des
	 * Saugsaums darueber (Nutzer 19.09.2026). Die Felder stehen am Punkt unter
	 * `extendedHeader.spiegelTiefe` und `extendedHeader.saugsaum`. Ein
	 * Wasserspiegel ueber Flur ist negativ (seit 02.10.2026, flur-tiefe.ts).
	 */
	/**
	 * Woraus der Zufall des Bildes kommt - die Kennung des Punktes.
	 *
	 * Ohne sie bekaeme JEDE Grube dieselbe Wurzel an derselben Stelle
	 * (Nutzer-Befund 20.09.2026). Die Kennung und nicht die Messwerte: So
	 * bleibt das Bild stabil, waehrend jemand die Aufnahme noch bearbeitet.
	 */
	saat?: string | number | null;
	/**
	 * Filz (filz-tiefen.ts): Die Auflage gehoert zum Profil, 0 cm liegt an
	 * ihrer Oberkante. Das Massband beginnt dann dort, und jede GENANNTE Tiefe
	 * (Tipp, Wasserstand) rueckt um `versatz` - die Gesamt-Auflage - nach
	 * unten. Gezeichnet wird unveraendert: Die Wand ist, wie sie ist, nur die
	 * Zahlen aendern sich (Nutzer-Entscheidung 25.09.2026).
	 */
	filz?: { versatz: number };
	wasser?: {
		spiegelTiefe?: number | null;
		saugsaum?: number | null;
		/**
		 * Der Tag der Aufnahme, schon fertig geschrieben („12.06.2026").
		 *
		 * Ein Wasserstand ist eine Messung von EINEM Tag - ohne Datum sieht er
		 * aus wie ein Dauerzustand (Nutzer-Wunsch 19.09.2026). Im Gastmodus
		 * bleibt er weg: Das Aufnahmedatum ist dort eine Personenangabe.
		 */
		datum?: string | null;
	};
}

export interface GrubenbildOptionen {
	/**
	 * Was festgehalten ist: der Index einer HORIZONTZEILE, oder „auflage:Ol".
	 *
	 * Ein Klick waehlt den Horizont, nicht den einzelnen Abschnitt
	 * (Nutzer-Entscheidung 19.09.2026): Der Horizont ist die fachliche Einheit,
	 * und die Tafel zeigt dann alle Substratschichten, die in ihm liegen.
	 */
	gewaehlt: number | string | null;
	substratgrenzen: boolean;
	textur: boolean;
	/*
	 * Die vier Anzeige-Schalter ueber dem Bild (Nutzer-Wunsch 24.09.2026).
	 * Alle freiwillig und ohne Angabe AN - wer nichts angibt, bekommt das Bild
	 * wie bisher.
	 */
	/**
	 * Die Grenzlinien: die dicken welligen Linien an den Horizontgrenzen UND
	 * die feinen Fugen an den Substratgrenzen, mit einem Schalter
	 * (Nutzer-Entscheidung 24.09.2026).
	 */
	grenzlinien?: boolean;
	/** Das Wurzelwerk. */
	wurzeln?: boolean;
	/**
	 * Die Horizonte: Farbe, Muster, Grenzlinien, weiche Uebergaenge und die
	 * Kuerzel. Aus: Die Wand bleibt neutral, nur der Humusgehalt des Substrats
	 * dunkelt ab - so sind die Substratschichten fuer sich zu sehen.
	 */
	horizonte?: boolean;
	/**
	 * Die Substrate: Textur, Steine, Fugen, Angaben rechts und die Abdunklung
	 * nach Humusgehalt. Aus: Die Farbe kommt allein vom Horizont, wie in der
	 * Legende unter dem Bild.
	 */
	substrate?: boolean;
	beiWahl: (was: number | string) => void;
	/** Der Tipp beim Zeigen erklaert die SUBSTRATSCHICHT unter dem Zeiger. */
	beiZeigen: (inhalt: string | null, x: number, y: number) => void;
	/**
	 * Klick auf die Beschriftung der Humusauflage (Ol, Of, Oh), mit dem Ort
	 * des Klicks im Fenster. Ohne Angabe ist der Block nicht anklickbar.
	 */
	beiAuflageSchild?: (x: number, y: number) => void;
}

// `el` und `streu` stehen in svg-werkzeug.ts - die Lupe braucht sie auch, und
// sie zeichnet die Humusauflage ins Grubenbild (`legeAuflageAusKacheln`).

function text(x: number, y: number, inhalt: string, attr?: Record<string, string | number>): SVGElement {
	const t = el('text', { x, y, 'font-size': 11, fill: '#2b2722', ...(attr ?? {}) });
	t.textContent = inhalt;
	return t;
}

/**
 * Aus der Kennung eines Punktes eine Zahl fuer den Zufall machen.
 *
 * Ohne Kennung bleibt es bei einem festen Wert - dann sieht jedes Bild gleich
 * aus, aber wenigstens immer gleich.
 */
function saatZahl(saat: string | number | null | undefined): number {
	if (typeof saat === 'number' && Number.isFinite(saat)) return Math.abs(Math.round(saat)) + 1;
	const text = typeof saat === 'string' ? saat : '';
	if (!text) return 4517;
	let h = 2166136261;
	for (let i = 0; i < text.length; i++) {
		h ^= text.charCodeAt(i);
		h = Math.imul(h, 16777619);
	}
	return (h >>> 0) || 4517;
}

function zahl(v: unknown): number {
	return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

/**
 * Wie viele cm das Wasser ueber der Oberkante der Auflage steht; 0, wenn es
 * darunter steht oder nichts gemessen ist. Die Spiegeltiefe zaehlt ab Oberkante
 * Mineralboden und ist ueber Flur negativ (flur-tiefe.ts).
 */
export function wasserUeberAuflage(spiegelTiefe: number | null | undefined, auflageCm: number): number {
	if (typeof spiegelTiefe !== 'number' || !Number.isFinite(spiegelTiefe)) return 0;
	return Math.max(0, -spiegelTiefe - auflageCm);
}

/** Wie weit eine Horizontgrenze um ihre Tiefe schwingen darf, in Pixel. */
const WELLE_PX = 1.6;

/**
 * So flach schwingt die Fuge einer Substratgrenze.
 *
 * Sie ist die zartere der beiden Linien; schwaenge sie so weit wie eine
 * Horizontgrenze, waeren die beiden im Bild nicht mehr auseinanderzuhalten.
 */
const FUGE_WELLE_PX = 0.9;

/**
 * Wie weit ein Farbuebergang hoechstens nach oben und unten reicht, in
 * Zentimetern. Der Streifen ist doppelt so hoch - er liegt mittig auf der
 * Grenze.
 */
const UEBERGANG_HOECHSTENS = 9;

/**
 * Breite des Maszbandes in Pixel.
 *
 * Sie ist NICHT maszstaeblich: Wie breit so ein Band wirklich ist, sagen die
 * Daten nicht, und geraten wird hier nichts. Die Hoehe der Felder ist dagegen
 * maszstabsgerecht - ein Feld ist genau ein Dezimeter, daran wird abgelesen.
 */
const BAND_BREITE = 26;

/** Breite der Zentimeter-Zaehnchen am rechten Bandrand, dicht an der Wand. */
const BAND_ZAEHNE = 7;

/** Unter diesem Maszstab wuerden die Zaehnchen zu einem grauen Strich. */
const ZAEHNE_AB_PX = 2;

/**
 * Das Schwarz der dunklen Felder - kein reines Schwarz.
 *
 * Ein echtes Band ist bedrucktes Kunststoffgewebe und in der Grube nie
 * tiefschwarz; daneben stehen die Horizontfarben, gegen die ein hartes Schwarz
 * zu laut waere.
 */
const BAND_DUNKEL = '#38332f';
const BAND_HELL = '#fbfaf7';

/**
 * Wie weit das Band seitlich schlaengelt, in Pixel.
 *
 * Es schlaengelt in kleinen Schritten, die aufeinander aufbauen, statt je Feld
 * neu zu springen (Nutzer-Befund 20.09.2026: „das band ist ungleichmaeszig
 * dick"). Springt jedes Feld fuer sich, sieht es aus, als waeren die Felder
 * verschieden breit - dabei ist das Band ueberall gleich breit und nur
 * versetzt.
 */
const BAND_SCHRITT = 0.35;
const BAND_AUSSCHLAG = 1.1;

/** So breit werden die zwei Ziffern gestaucht. */
const BAND_ZAHL_BREITE = 11;

/** Wie weit der Erdreich-Streifen neben der Wand reicht. */
const ERDREICH_BREITE = BAND_BREITE + 8;

/**
 * Eine leicht wellige Linie statt eines schnurgeraden Strichs.
 *
 * Eine Horizontgrenze ist im Gelaende fast nie ein Lineal-Strich. Die Welle
 * bleibt aber klein (rund einen halben Zentimeter im Maszstab) und schwingt um
 * die erfasste Tiefe - die Linie soll natuerlicher wirken, nicht die Tiefe
 * unleserlich machen. Ausnahme bleibt bewusst die Gelaendeoberflaeche und die
 * Grubensohle: Das sind gemessene Bezugslinien, keine Bodenmerkmale.
 *
 * `hub` sagt, wie weit sie ausschlaegt. Eine Substratgrenze schwingt flacher
 * als eine Horizontgrenze - sie ist die zartere der beiden und soll ihr nicht
 * die Schau stehlen (Nutzer-Wunsch 20.09.2026).
 */
function welle(x: number, y: number, breite: number, r: () => number, hub = WELLE_PX): string {
	const schritt = 26;
	let d = `M${x.toFixed(1)} ${(y + (r() - 0.5) * hub).toFixed(1)}`;
	for (let px = x; px < x + breite; px += schritt) {
		const nx = Math.min(px + schritt, x + breite);
		d += ` Q${(px + schritt / 2).toFixed(1)} ${(y + (r() - 0.5) * 2 * hub).toFixed(1)}`
			+ ` ${nx.toFixed(1)} ${(y + (r() - 0.5) * hub).toFixed(1)}`;
	}
	return d;
}

export function zeichneGrubenbild(daten: GrubenbildDaten, opt: GrubenbildOptionen): SVGElement {
	const abs = daten.abschnitte;
	const auflage = daten.auflage;
	// Filz: um so viel ruecken alle genannten Tiefen nach unten (0 sonst).
	const versatz = daten.filz?.versatz ?? 0;
	const tiefe = abs.length ? abs[abs.length - 1].bis : GRUBE_CM;
	// Steht das Wasser ueber der Auflage, braucht es oben so viel Platz mehr
	// (Spiegeltiefe ueber Flur, 02.10.2026, siehe „Wasserstand" weiter unten).
	const ueberstand = wasserUeberAuflage(daten.wasser?.spiegelTiefe, auflage.gesamt);
	const px = maszstabFuer(tiefe + auflage.gesamt + ueberstand);

	const oben = RAND_OBEN + (auflage.gesamt + ueberstand) * px;
	const wandHoehe = tiefe * px;
	const wandBreite = BREITE_CM * px;
	const hoeheGesamt = oben + wandHoehe + RAND_UNTEN;

	// Die Anzeige-Schalter: ohne Angabe an.
	const mitHorizonten = opt.horizonte !== false;
	const mitSubstraten = opt.substrate !== false;
	// Ein Schalter fuer beide Sorten Grenzlinien; und ohne Horizonte gibt es
	// ohnehin keine Linien an ihren Grenzen.
	const mitGrenzlinien = opt.grenzlinien !== false;
	const mitHorizontlinien = mitHorizonten && mitGrenzlinien;
	const mitWurzeln = opt.wurzeln !== false;

	// Der rechte Rand traegt die ganze Beschriftung und richtet sich deshalb
	// nach ihr: Er ist so breit, wie die beiden Spuren es brauchen. Was
	// ausgeblendet ist, wird auch nicht beschriftet.
	const plan = beschriftungsPlan({
		...daten,
		horizonte: mitHorizonten ? daten.horizonte : [],
		substrate: mitSubstraten ? daten.substrate : [],
	}, px, tiefe, oben, wandBreite);
	const breiteGesamt = RAND_LINKS + wandBreite + plan.randRechts;
	const x0 = RAND_LINKS;
	const cmZuY = (cm: number) => oben + cm * px;

	// Auf einem schmalen Schirm schrumpft das Bild mit, statt hinauszuragen
	// (Nutzer-Entscheidung 19.09.2026). Das geht ueber die `viewBox`: Das CSS
	// setzt `width: 100%`, die Obergrenze ist die natuerliche Breite - auf
	// einem breiten Schirm aendert sich also nichts.
	//
	// Der Maszstab bleibt dabei richtig: Seitenverhaeltnis und Bemaszung
	// skalieren mit, und die Bemaszung nennt die Tiefe in Metern. „1,50 m
	// breit" heisst die Breite der Grube, nicht die auf dem Schirm.
	const svg = el('svg', {
		width: breiteGesamt, height: hoeheGesamt,
		viewBox: `0 0 ${breiteGesamt} ${hoeheGesamt}`,
		preserveAspectRatio: 'xMidYMin meet',
		class: 'grubenbild-svg',
		style: `max-width: ${Math.round(breiteGesamt)}px`,
	});
	const defs = el('defs');
	svg.appendChild(defs);
	defs.appendChild(el('pattern', {
		id: 'gb-umfeld', width: 6, height: 6, patternUnits: 'userSpaceOnUse',
		patternTransform: 'rotate(45)',
	}, [el('line', { x1: 0, y1: 0, x2: 0, y2: 6, stroke: '#c7c0b1', 'stroke-width': 1 })]));

	// ---------------- Bemassung oben: 1,50 m --------------------------------
	const bemY = 22;
	svg.appendChild(el('line', { x1: x0, y1: bemY, x2: x0 + wandBreite, y2: bemY, stroke: '#6c6660', 'stroke-width': 1 }));
	([[x0, 1], [x0 + wandBreite, -1]] as [number, number][]).forEach(([x, r]) => {
		svg.appendChild(el('path', { d: `M${x} ${bemY} l${6 * r} -3.5 v7 z`, fill: '#6c6660' }));
		svg.appendChild(el('line', { x1: x, y1: bemY - 8, x2: x, y2: bemY + 8, stroke: '#6c6660', 'stroke-width': 1 }));
	});
	const mitteX = x0 + wandBreite / 2;
	svg.appendChild(el('rect', { x: mitteX - 26, y: bemY - 9, width: 52, height: 15, class: 'gb-textgrund' }));
	svg.appendChild(text(mitteX, bemY + 3, '1,50 m', { 'text-anchor': 'middle', 'font-size': 11.5, fill: '#4a453f' }));

	// ---------------- Humusauflage ------------------------------------------
	// Dieselben Texturen wie in der Lupe, verkleinert (Nutzer-Wunsch
	// 25.09.2026: „die gleichen Texturen wie in der Auflagen-Vorschau, nur
	// kleinskaliert"): Die Bausteine der Lupe kommen als Kacheln auf den
	// Maszstab des Grubenbildes - Of und Oh als Muster, die Streu in
	// zufaelliger Folge, damit sich kein Blatt erkennbar wiederholt
	// (`legeAuflageAusKacheln`; die Folge haengt am Punkt). Das Bild ist EINE
	// Gruppe fuer alle Lagen; je Lage liegt darueber eine durchsichtige
	// Flaeche, die Zeigen und Klick faengt und beim Zeigen aufleuchtet - wie
	// bei jeder Schicht der Wand.
	const auflageBild = legeAuflageAusKacheln(defs, auflage.zeilen, x0, oben, wandBreite, px,
		streu(saatZahl(daten.saat) * 5 + 3));
	if (auflageBild) svg.appendChild(auflageBild);
	auflage.zeilen.forEach(z => {
		const y = oben - z.oben * px;
		const h = z.maechtigkeit * px;
		const eintrag = HORIZONT_FARBEN[z.feld];
		const g = el('g', { class: 'gb-schicht' });
		g.appendChild(el('rect', { x: x0, y, width: wandBreite, height: h, class: 'gb-aufleuchten' }));
		verdrahte(g, opt, 'auflage:' + z.feld,
			`${z.feld}, ${z.maechtigkeit} cm\n${eintrag.text}\n${daten.filz ? 'Humusauflage, gehört zum Profil (Filz)' : 'Humusauflage, liegt über der Geländeoberfläche'}`);
		svg.appendChild(g);
	});

	// Die Beschriftung der Auflage steht in denselben beiden Spuren wie alles
	// andere und wird deshalb ganz am Ende gezeichnet (siehe `beschrifte`).
	// Nur, wenn wirklich nichts eingetragen ist. Steht in Ol, Of oder Oh eine
	// 0, ist die Auflage erfasst - sie ist dann eben nicht da, und darauf muss
	// niemand hingewiesen werden (Nutzer-Befund 20.09.2026).
	if (!auflage.zeilen.length && !auflage.erfasst) {
		svg.appendChild(text(x0 + 7, oben - 6, 'keine Humusauflage erfasst',
			{ 'font-size': 10, fill: '#8a847c', 'font-style': 'italic' }));
	}

	// ---------------- Erdreich links und rechts -----------------------------
	// Links reicht er weiter: Dort haengt das Maszband, und es soll vor Boden
	// haengen, nicht vor der weiszen Flaeche daneben.
	([[x0 - ERDREICH_BREITE, ERDREICH_BREITE], [x0 + wandBreite, 16]] as [number, number][])
		.forEach(([x, b]) => {
			svg.appendChild(el('rect', { x, y: oben, width: b, height: wandHoehe, fill: 'url(#gb-umfeld)', opacity: 0.55 }));
		});

	// ---------------- Die Wand ----------------------------------------------
	// Was der Horizont SAGT - unabhaengig davon, ob er gezeichnet wird. Daran
	// haengen die Wurzeln: Sie gehoeren zum Boden, nicht zur Einfaerbung.
	const horizontBild = abs.map(a => schichtFarbe(a.horizont?.horizonte, a.substrat?.humusgehalt));
	// Was die Wand ZEIGT. Ohne Horizonte bleibt der neutrale Boden, den nur der
	// Humusgehalt des Substrats toent; ohne Substrate faellt diese Toenung weg,
	// und die Farbe ist die des Horizonts, wie in der Legende unter dem Bild.
	const farben = mitHorizonten && mitSubstraten
		? horizontBild
		: abs.map(a => schichtFarbe(
			mitHorizonten ? a.horizont?.horizonte : null,
			mitSubstraten ? a.substrat?.humusgehalt : undefined,
		));
	const substratNr = abs.map(a => daten.substrate.indexOf(a.substrat as SubstratZeile));
	// Die Steine je Schicht - gezeichnet werden sie erst nach den Uebergaengen.
	const steinLagen: SVGElement[] = [];
	// Welche Substrate wechseln laut Wechsel-Haken miteinander? Daraus werden
	// die Baender eines Bb-Horizontes abgeleitet.
	const bereiche = wechselbereiche(daten.substrate);

	abs.forEach((a, i) => {
		const y = cmZuY(a.von);
		const h = (a.bis - a.von) * px;
		if (h <= 0) return;
		const hf = farben[i];
		// Jede zweite Substratschicht eine Spur heller. Sehr dezent: Es soll die
		// Schichten trennen, nicht die Horizontfarbe verfaelschen.
		const stufe = mitSubstraten && substratNr[i] >= 0 && substratNr[i] % 2 === 1 ? 1.8 : 0;
		const g = el('g', { class: 'gb-schicht' });
		g.appendChild(el('rect', {
			x: x0, y, width: wandBreite, height: h,
			fill: stufe ? tonen(hf.farbe, stufe) : hf.farbe,
		}));

		// Baender in Pixel-Koordinaten dieses Abschnitts.
		const bereich = hf.muster === 'baender' ? bereichBei(bereiche, (a.von + a.bis) / 2) : null;
		const grund = bereich ? grundmasse(bereich) : null;
		const baender = bereich
			? baenderImAbschnitt(bereich, a.von, a.bis, hf.baenderDichte).map(bd => ({
				lage: bd.lage, von: cmZuY(bd.von), bis: cmZuY(bd.bis), grundmasse: bd.lage === grund,
			}))
			: null;

		muster(g, defs, hf.muster, x0, y, wandBreite, h, i, hf.musterStaerke, baender, hf.baenderDichte);
		// Sind die Baender schon aus der Wechselkette gezeichnet, faellt die
		// zusaetzliche Baenderung der Wechselschichtung weg: Beides zusammen
		// zeigt DASSELBE zweimal, und das Bild sieht aus wie Wellblech.
		if (opt.textur && mitSubstraten) {
			const steinLage = textur(g, defs, a.substrat, x0, y, wandBreite, h, i, px, !!baender?.length);
			if (steinLage) steinLagen.push(steinLage);
		}
		g.appendChild(el('rect', { x: x0, y, width: wandBreite, height: h, class: 'gb-aufleuchten' }));
		// Der Klick waehlt den HORIZONT dieses Abschnitts, der Tipp erklaert
		// seine Substratschicht.
		const horizontNr = a.horizont ? daten.horizonte.indexOf(a.horizont) : -1;
		verdrahte(g, opt, horizontNr, substratTipp(a, wechselLage(daten.substrate, substratNr[i]), versatz));
		svg.appendChild(g);
	});

	// ---------------- Weiche Uebergaenge ------------------------------------
	// Im Gelaende ist eine Horizontgrenze selten ein Strich; die Farben gehen
	// ineinander ueber. Ueber jede Grenze kommt deshalb ein schmaler Streifen,
	// in dem beide Farben verlaufen. Die Grenzlinie bleibt trotzdem stehen -
	// sonst waere nicht mehr abzulesen, wo der Horizont wechselt.
	//
	// Ohne Horizonte gibt es keine Uebergaenge: Was dann noch an Farbe wechselt,
	// ist der Humusgehalt von Substratschicht zu Substratschicht, und die
	// trennt ihre Fuge.
	abs.forEach((a, i) => {
		if (i === 0 || !mitHorizonten) return;
		const obenFarbe = farben[i - 1].farbe, untenFarbe = farben[i].farbe;
		if (obenFarbe === untenFarbe) return;
		const hOben = (abs[i - 1].bis - abs[i - 1].von) * px;
		const hUnten = (a.bis - a.von) * px;
		// Breit genug, um wie ein Uebergang zu wirken: Im Gelaende laufen zwei
		// Horizonte oft ueber etliche Zentimeter ineinander, und dieser Verlauf
		// ist in den Daten gar nicht erfasst (Hinweis des Kartierers,
		// 19.09.2026). Begrenzt wird er von der duenneren Nachbarschicht - eine
		// 5-cm-Lage darf nicht im Verlauf ihrer Nachbarn verschwinden.
		const d = Math.max(3, Math.min(UEBERGANG_HOECHSTENS * px, hOben * 0.42, hUnten * 0.42));
		const id = `gb-verlauf${i}`;
		const mitte = mischen(obenFarbe, untenFarbe);
		defs.appendChild(el('linearGradient', { id, x1: 0, y1: 0, x2: 0, y2: 1 }, [
			el('stop', { offset: '0%', 'stop-color': obenFarbe, 'stop-opacity': 0 }),
			el('stop', { offset: '28%', 'stop-color': mischen(obenFarbe, mitte), 'stop-opacity': 0.55 }),
			el('stop', { offset: '50%', 'stop-color': mitte, 'stop-opacity': 0.88 }),
			el('stop', { offset: '72%', 'stop-color': mischen(mitte, untenFarbe), 'stop-opacity': 0.55 }),
			el('stop', { offset: '100%', 'stop-color': untenFarbe, 'stop-opacity': 0 }),
		]));
		svg.appendChild(el('rect', {
			x: x0, y: cmZuY(a.von) - d, width: wandBreite, height: d * 2, fill: `url(#${id})`,
		}));
	});

	// ---------------- Steine, obenauf -------------------------------------
	// Nach den weichen Uebergaengen: Sonst laege der Verlauf ueber ihnen, und
	// sie wuerden an jeder Horizontgrenze blasser (Nutzer-Befund 21.09.2026).
	steinLagen.forEach(s => svg.appendChild(s));

	// ---------------- Wurzeln ueber die Schichten hinweg --------------------
	// Sie beginnen an der Gelaendeoberflaeche und laufen durch, solange der
	// Horizont durchwurzelt ist. Ein Strang, der an jeder Schichtgrenze neu
	// anfaengt, sieht aus wie abgeschnitten.
	//
	// NACH den weichen Uebergaengen gezeichnet: Davor lag der Verlaufsstreifen
	// ueber ihnen, und die Wurzeln wurden an jeder Horizontgrenze blasser
	// (Nutzer-Befund 19.09.2026).
	//
	// Gemessen wird am Horizont, wie er ERFASST ist (`horizontBild`), nicht an
	// der Einfaerbung: Wer die Horizonte ausblendet, behaelt die Wurzeln - dafuer
	// gibt es den eigenen Schalter.
	const krautTiefe = mitWurzeln ? durchwurzeltBis(abs, horizontBild) : 0;
	if (krautTiefe > 0) {
		const baumTiefe = baumwurzelBis(abs, tiefe, daten.wasser?.spiegelTiefe);
		const gw = el('g', { class: 'gb-wurzeln' });
		const clipId = 'gb-wurzelclip';
		// Der Ausschnitt reicht UEBER die Gelaendeoberflaeche hinauf, durch die
		// ganze Humusauflage: Ol, Of und Oh sind durchwurzelt, im Oh sitzt der
		// dichteste Wurzelfilz. Vorher endete der Ausschnitt bei 0 cm, und die
		// Wurzeln waren an der Oberflaeche abgeschnitten, obwohl darueber noch
		// Auflage liegt (Nutzer-Befund 20.09.2026).
		const auflageH = auflage.gesamt * px;
		defs.appendChild(el('clipPath', { id: clipId }, [
			el('rect', {
				x: x0, y: oben - auflageH, width: wandBreite,
				height: auflageH + Math.max(krautTiefe, baumTiefe) * px,
			}),
		]));
		gw.setAttribute('clip-path', `url(#${clipId})`);
		wurzeln(gw, x0, oben, wandBreite, px, baumTiefe, krautTiefe,
			auflage.gesamt, streu(saatZahl(daten.saat)));
		svg.appendChild(gw);
	}

	// ---------------- Grenzen -----------------------------------------------
	// Substratgrenze als Fuge: dunkler Strich mit heller Lichtkante darunter. So
	// ist jede Substratschicht einzeln zu erkennen, auch wenn zwei benachbarte
	// dieselbe Bodenart fuehren. Beide schwingen leicht - flacher als eine
	// Horizontgrenze, und beide auf DERSELBEN Welle: Die Lichtkante ist die um
	// einen Pixel versetzte Kopie, eine zweite Welle schaute an den
	// Schwingungen hervor.
	//
	// Faellt eine Substratgrenze mit einer HORIZONTgrenze zusammen - was oft so
	// ist -, entfaellt die Fuge: Die Horizontlinie trennt dort schon, und sie
	// ist wellig. Die gerade helle Lichtkante schaute unter ihr hervor
	// (Nutzer-Befund 20.09.2026: „unter den schwarzen Horizontbegrenzungslinien
	// schimmert ein heller gerader Strich durch").
	//
	// Nur eine Horizontlinie, die auch GEZEICHNET wird, ersetzt die Fuge: Sind
	// die Linien ausgeblendet, bekommt jede Substratgrenze ihre Fuge - sonst
	// waere die zusammenfallende unsichtbar.
	const horizontgrenzen = mitHorizontlinien
		? daten.horizonte.map(z => zahl(z.tiefe)).filter(t => t > 0 && t < tiefe)
		: [];
	if (opt.substratgrenzen && mitSubstraten && mitGrenzlinien) {
		daten.substrate.forEach(z => {
			const t = zahl(z.tiefe);
			if (t <= 0 || t >= tiefe) return;
			// Naeher als zwei Pixel liegen die Linien ohnehin uebereinander.
			if (horizontgrenzen.some(h => Math.abs(h - t) * px < 2)) return;
			const d = welle(x0, cmZuY(t), wandBreite,
				streu(Math.round(t * 10) * 613 + 11), FUGE_WELLE_PX);
			svg.appendChild(el('g', { class: 'gb-substratgrenze' }, [
				el('path', { d, fill: 'none', stroke: 'rgba(32,26,18,.5)', 'stroke-width': 1 }),
				el('path', {
					d, fill: 'none', stroke: 'rgba(255,252,244,.42)', 'stroke-width': 1,
					transform: 'translate(0 1)',
				}),
			]));
		});
	}
	if (mitHorizontlinien) {
		daten.horizonte.forEach(z => {
			const t = zahl(z.tiefe);
			if (t <= 0 || t >= tiefe) return;
			svg.appendChild(el('path', {
				class: 'gb-horizontgrenze',
				d: welle(x0, cmZuY(t), wandBreite, streu(Math.round(t) * 977 + 5)),
				fill: 'none', stroke: 'rgba(28,24,18,.8)', 'stroke-width': 1.8,
			}));
		});
	}

	// ---------------- Wasserstand -------------------------------------------
	// `spiegelTiefe` ist der Wasserspiegel, der `saugsaum` steht darueber
	// (Nutzer 19.09.2026); beide in cm unter Gelaendeoberflaeche, beide von
	// Hand erfasst. Ohne Messwert wird nichts gezeichnet - ein Wasserstand,
	// den niemand gemessen hat, waere eine erfundene Auskunft.
	//
	// Ueber Flur (02.10.2026) ist die Spiegeltiefe negativ, gezaehlt wie jede
	// Tiefe ab Oberkante Mineralboden (flur-tiefe.ts). Gezeichnet wird dieselbe
	// Rechnung nach oben: Ohne Filz liegt der Spiegel dann ueber dem Mineralboden
	// (auch mitten in der Auflage), bei einem Filz zaehlt die Zeile ab Oberkante
	// Auflage - wie jede andere Zahl im Bild (`versatz`). Ueber der Oberflaeche
	// steht offenes Wasser.
	const spiegelRoh = daten.wasser?.spiegelTiefe;
	const gemessen = typeof spiegelRoh === 'number' && Number.isFinite(spiegelRoh);
	const spiegel = zahl(spiegelRoh);
	const saugsaumAb = zahl(daten.wasser?.saugsaum);
	if (gemessen && spiegel < tiefe) {
		const yS = cmZuY(spiegel);
		const yOberflaeche = oben - auflage.gesamt * px;
		if (yS < yOberflaeche) {
			svg.appendChild(el('rect', {
				class: 'gb-offenes-wasser',
				x: x0, y: yS, width: wandBreite, height: yOberflaeche - yS,
				fill: WASSER_FARBE, opacity: 0.18,
			}));
		}

		// Der Saugsaum darueber: fein gepunktet ueber die Bodenfarbe gelegt,
		// OHNE Beschriftung und OHNE Trennlinie (Nutzer-Entscheidung
		// 19.09.2026). Er hat keine scharfe Grenze - das Wasser steigt im
		// Porenraum auf und wird nach oben weniger. Genau das sagen Punkte,
		// die nach unten dichter stehen; ein Strich behauptete eine Grenze,
		// die es nicht gibt.
		if (saugsaumAb > 0 && saugsaumAb < spiegel) {
			const yO = cmZuY(saugsaumAb);
			const hoehe = yS - yO;
			const rs = streu(Math.round(saugsaumAb) * 7919 + 31);
			const gr = Math.max(0.7, px * 0.28);
			const zahl = Math.round(((wandBreite / px) * (spiegel - saugsaumAb)) / 1.6);
			const punkte: string[] = [];
			for (let i = 0; i < zahl; i++) {
				// Wurzel aus dem Zufallswert: nach unten gleichmaessig dichter.
				const py = yO + Math.sqrt(rs()) * hoehe;
				const pxx = x0 + rs() * wandBreite;
				punkte.push(`M${pxx.toFixed(1)} ${py.toFixed(1)}h${gr.toFixed(2)}v${gr.toFixed(2)}h-${gr.toFixed(2)}z`);
			}
			svg.appendChild(el('path', { d: punkte.join(''), fill: WASSER_LINIE, opacity: 0.62 }));
		}

		// Unter dem Spiegel steht Wasser: ein Schleier ueber allem, was dort
		// liegt.
		svg.appendChild(el('rect', {
			x: x0, y: yS, width: wandBreite, height: oben + wandHoehe - yS,
			fill: WASSER_FARBE, opacity: 0.2,
		}));
		svg.appendChild(el('line', {
			x1: x0, y1: yS, x2: x0 + wandBreite, y2: yS, stroke: WASSER_LINIE, 'stroke-width': 1.6,
		}));
		// Das uebliche Zeichen fuer einen Wasserspiegel.
		const mx = x0 + wandBreite - 20;
		svg.appendChild(el('path', {
			d: `M${mx - 6.5} ${yS - 1.5}L${mx + 6.5} ${yS - 1.5}L${mx} ${yS + 8}Z`,
			fill: WASSER_LINIE,
		}));
		const wann = daten.wasser?.datum ? ` (${daten.wasser.datum})` : '';
		// Die Zeile steht wie die Horizontkuerzel links in der Wand und weicht
		// nach derselben Regel zur Seite aus: Steht ein Kuerzel in ihrer Zeile,
		// rueckt sie dahinter (Nutzer-Befund 20.09.2026). Die Hoehe bleibt - sie
		// gehoert zum Wasserspiegel und darf nicht wandern.
		const wx = wandAusweichenX(plan.inDerWand, yS - 8);
		svg.appendChild(text(x0 + wx, yS - 5, `Wasserstand ${wasserstandText(angezeigteTiefe(spiegel, versatz) ?? spiegel)}${wann}`,
			{ 'font-size': 10.5, fill: WASSER_LINIE, 'font-weight': 600 }));
	}

	// Eine Marke bei 2 m gab es hier bis zum 19.09.2026 („Grubensohle"). Sie ist
	// auf Wunsch des Nutzers wieder raus: Das Bild zeigt die Aufnahme, und die
	// ist durchgehend - wo gegraben und wo gebohrt wurde, steht an der Punktart.

	// ---------------- Hervorhebung des gewaehlten Horizontes ----------------
	if (typeof opt.gewaehlt === 'number' && daten.horizonte[opt.gewaehlt]) {
		const i = opt.gewaehlt;
		const von = i > 0 ? zahl(daten.horizonte[i - 1].tiefe) : 0;
		const bis = Math.min(zahl(daten.horizonte[i].tiefe), tiefe);
		if (bis > von) {
			const y = cmZuY(von);
			const h = (bis - von) * px;
			svg.appendChild(el('rect', {
				x: x0 - 2, y: y - 2, width: wandBreite + 4, height: h + 4,
				fill: 'none', stroke: '#1b60a8', 'stroke-width': 2.5, rx: 2,
			}));
			svg.appendChild(el('rect', { x: x0, y, width: wandBreite, height: h, fill: '#1b60a8', opacity: 0.1 }));
		}
	}

	svg.appendChild(el('rect', {
		x: x0, y: oben, width: wandBreite, height: wandHoehe,
		fill: 'none', stroke: '#2b2722', 'stroke-width': 1.5,
	}));
	svg.appendChild(el('line', {
		x1: x0 - ERDREICH_BREITE, y1: oben, x2: x0 + wandBreite + 16, y2: oben,
		stroke: '#2b2722', 'stroke-width': 2,
	}));

	// ---------------- Tiefenskala: das Grubenmaszband -----------------------
	// Bei einem Filz haengt das Band von der Oberkante der Auflage: Dort liegt
	// 0 cm, und die Dezimeter zaehlen von dort (Nutzer-Entscheidung 25.09.2026).
	if (daten.filz) {
		maszband(svg, x0, oben - auflage.gesamt * px, tiefe + auflage.gesamt, px, streu(saatZahl(daten.saat) * 3 + 7));
		svg.appendChild(text(x0 - BAND_BREITE, oben + wandHoehe + 13, FILZ_MASSBAND_TEXT,
			{ 'font-size': 9, fill: '#4a453f' }));
	} else {
		maszband(svg, x0, oben, tiefe, px, streu(saatZahl(daten.saat) * 3 + 7));
	}

	const bx = 16;
	svg.appendChild(el('line', { x1: bx, y1: oben, x2: bx, y2: oben + wandHoehe, stroke: '#6c6660', 'stroke-width': 1 }));
	([[oben, 1], [oben + wandHoehe, -1]] as [number, number][]).forEach(([y, r]) => {
		svg.appendChild(el('path', { d: `M${bx} ${y} l-3.5 ${6 * r} h7 z`, fill: '#6c6660' }));
		svg.appendChild(el('line', { x1: bx - 7, y1: y, x2: bx + 7, y2: y, stroke: '#6c6660', 'stroke-width': 1 }));
	});
	const my = oben + wandHoehe / 2;
	svg.appendChild(el('rect', { x: bx - 8, y: my - 30, width: 16, height: 60, class: 'gb-textgrund' }));
	const t2 = text(0, 0, meterText(tiefe), { 'font-size': 11.5, fill: '#4a453f', 'text-anchor': 'middle' });
	t2.setAttribute('transform', `translate(${bx + 4} ${my}) rotate(-90)`);
	svg.appendChild(t2);

	beschrifte(svg, plan, x0, wandBreite);
	auflageSchild(svg, plan, x0 + wandBreite, opt);
	return svg;
}

/**
 * Eine unsichtbare Flaeche ueber der Beschriftung der Humusauflage. Ein Klick
 * darauf zeigt Ol, Of und Oh vergroessert (Nutzer-Wunsch 21.09.2026).
 *
 * Sie liegt als LETZTES im Bild: Die Beschriftung darunter faengt sonst den
 * Klick ab. Die Fuellung steht als Attribut da - ein `<rect>` ohne `fill` ist
 * im SVG schwarz.
 */
function auflageSchild(
	svg: SVGElement, plan: BeschriftungsPlan, kante: number, opt: GrubenbildOptionen,
): void {
	const block = plan.auflageBlock;
	const melde = opt.beiAuflageSchild;
	if (!block || !melde) return;
	const g = el('g', { class: 'gb-auflage-schild' });
	const titel = el('title');
	titel.textContent = 'Humusauflage vergrößert zeigen';
	g.appendChild(titel);
	g.appendChild(el('rect', {
		x: kante + block.vonX, y: block.vonY,
		width: block.bisX - block.vonX, height: block.bisY - block.vonY,
		rx: 3, fill: '#0d6efd', 'fill-opacity': 0, 'pointer-events': 'all',
		class: 'gb-auflage-flaeche',
	}));
	g.addEventListener('click', e => {
		const m = e as MouseEvent;
		opt.beiZeigen(null, 0, 0);
		melde(m.clientX, m.clientY);
	});
	svg.appendChild(g);
}

function verdrahte(g: SVGElement, opt: GrubenbildOptionen, was: number | string, tipp: string): void {
	// Nach dem Klick geht der Tipp weg (Nutzer-Entscheidung 19.09.2026): Die
	// Angaben stehen jetzt in der Tafel, und ein schwarzes Feld ueber dem Bild
	// verdeckt genau das, was man gerade angesehen hat.
	g.addEventListener('click', () => {
		opt.beiZeigen(null, 0, 0);
		opt.beiWahl(was);
	});
	const zeigen = (e: Event) => {
		const m = e as MouseEvent;
		opt.beiZeigen(tipp, m.clientX, m.clientY);
	};
	g.addEventListener('mouseenter', zeigen);
	g.addEventListener('mousemove', zeigen);
	g.addEventListener('mouseleave', () => opt.beiZeigen(null, 0, 0));
}

/**
 * Das Grubenmaszband an der linken Wandkante.
 *
 * Nachgebildet ist das Band, das in der Grube wirklich an der Wand haengt
 * (Vorlage vom Nutzer, 20.09.2026): schwarze und weisze Felder im Wechsel, je
 * eines einen Dezimeter hoch, die Dezimeterzahl zweistellig mit fuehrender Null
 * unten im Feld und in der Gegenfarbe, am rechten Bandrand eine Kammleiste aus
 * Zentimeter-Zaehnchen, oben ein Schild.
 *
 * Die Zentimeterskala liegt rechts, also an der Wand (Nutzer-Wunsch
 * 20.09.2026): Dort wird abgelesen, und die Zaehnchen stehen dann unmittelbar
 * neben der Schicht, deren Tiefe man sucht. Die Dezimeterzahl rueckt dafuer
 * nach links.
 *
 * Abgelesen wird an der UNTERKANTE eines Feldes: Wo „01" steht, endet der erste
 * Dezimeter, also 10 cm unter der Gelaendeoberflaeche.
 *
 * Das Band haengt nie schnurgerade, deshalb sitzt jedes Feld ein wenig anders
 * (Nutzer-Wunsch: „darf ruhig ein bisschen krumpelig aussehen"). Der Versatz
 * ist seitlich und aendert die Tiefen nicht; er kommt aus der Kennung des
 * Punktes, dasselbe Profil sieht also immer gleich aus.
 */
function maszband(
	svg: SVGElement, x0: number, oben: number, tiefe: number, px: number, r: () => number,
): void {
	const g = el('g', { class: 'gb-maszband' });
	const kante = x0;

	// Das Schild am oberen Ende - auf dem echten Band steht dort die Nummer der
	// Grube. Hier traegt es die Einheit: Die Zahlen sind Dezimeter.
	let versatz = 0;
	g.appendChild(el('rect', {
		x: kante - BAND_BREITE, y: oben - 20, width: BAND_BREITE, height: 15,
		fill: BAND_HELL, stroke: '#8a847c', 'stroke-width': 0.6,
	}));
	g.appendChild(text(kante - BAND_BREITE / 2, oben - 9, 'dm',
		{ 'text-anchor': 'middle', 'font-size': 9.5, fill: '#4a453f' }));

	for (let dm = 0; dm * 10 < tiefe; dm++) {
		const von = dm * 10;
		const bis = Math.min(von + 10, tiefe);
		const y = oben + von * px;
		const h = (bis - von) * px;
		const dunkel = dm % 2 === 0;
		const gegen = dunkel ? BAND_HELL : BAND_DUNKEL;
		// Ein Schritt weiter in dieselbe Richtung, nicht ein neuer Sprung: So
		// bleibt das Band ueberall gleich breit und haengt trotzdem nicht
		// schnurgerade.
		versatz = Math.max(-BAND_AUSSCHLAG, Math.min(BAND_AUSSCHLAG,
			versatz + (r() - 0.5) * 2 * BAND_SCHRITT));
		const bx = kante - BAND_BREITE + versatz;

		g.appendChild(el('rect', {
			x: bx, y, width: BAND_BREITE, height: h,
			fill: dunkel ? BAND_DUNKEL : BAND_HELL, stroke: '#6c6660', 'stroke-width': 0.4,
		}));

		// Die Zentimeter-Zaehnchen: ein Zahn, eine Luecke, jeweils einen
		// Zentimeter. Bei kleinem Maszstab werden sie zu einem grauen Strich -
		// dann lieber keine.
		//
		// Der Takt springt am Feldwechsel um einen Zentimeter, weil die Farben
		// sich dort umkehren: Im hellen Feld ist der ZAHN dunkel, im dunklen die
		// LUECKE. Ohne den Versatz traefen am Dezimeterwechsel zwei dunkle
		// Zentimeter aufeinander (Nutzer-Befund 20.09.2026) - der Kamm soll
		// durchgehend wechseln, ueber die Feldgrenze hinweg.
		if (px >= ZAEHNE_AB_PX) {
			for (let cm = von + (dunkel ? 1 : 0); cm < bis; cm += 2) {
				g.appendChild(el('rect', {
					x: bx + BAND_BREITE - BAND_ZAEHNE, y: oben + cm * px, width: BAND_ZAEHNE,
					height: Math.min(px, (bis - cm) * px), fill: gegen,
				}));
			}
		}

		// Beschriftet wird nur ein ganzes Feld - eine Zahl am angeschnittenen
		// letzten Feld staende an einer Tiefe, die gar nicht erfasst ist.
		//
		// Sonst traegt JEDER Dezimeter seine Zahl, wie am echten Band. Platz ist
		// dafuer immer: `maszstabFuer` geht nie unter 1,5 px je Zentimeter, ein
		// Feld ist also mindestens 15 Pixel hoch.
		if (bis - von >= 9.5) {
			// Etwas schmaler gestaucht als die Schrift von Haus aus laeuft
			// (Nutzer-Wunsch): Zwei Ziffern sollen ruhig im Feld sitzen und
			// nicht bis an die Zaehnchen reichen.
			const zahl = text(
				bx + (BAND_BREITE - BAND_ZAEHNE) / 2, y + h - 3.5,
				String(dm + 1).padStart(2, '0'),
				{ 'text-anchor': 'middle', 'font-size': 9.5, 'font-weight': 700, fill: gegen },
			);
			zahl.setAttribute('textLength', String(BAND_ZAHL_BREITE));
			zahl.setAttribute('lengthAdjust', 'spacingAndGlyphs');
			g.appendChild(zahl);
		}
	}

	svg.appendChild(g);
}

/** Die Beschriftung ist immer schwarz (Nutzer-Entscheidung 20.09.2026). */
const SCHRIFT_FARBE = '#000';

/**
 * Ein zarter heller Halo um die Schrift - bei JEDER Beschriftung, auch auf
 * hellem Grund (Nutzer-Entscheidung 20.09.2026).
 *
 * Er hebt die Buchstaben von der Schicht ab, auf der sie stehen, ohne dass die
 * Schriftfarbe wechseln muss. Ihn nur dort zu setzen, wo der Grund dunkel ist,
 * sah unruhig aus: Dieselbe Beschriftung saehe dann von Schicht zu Schicht
 * anders aus.
 */
const HALO = {
	stroke: '#fff', 'stroke-width': 1.3, 'stroke-linejoin': 'round',
	'stroke-opacity': 0.85, 'paint-order': 'stroke',
};

/** Eine duenne Fuehrungslinie von der Schicht zu ihrer Beschriftung. */
function fuehrung(x1: number, y1: number, x2: number, y2: number): SVGElement {
	return el('line', { x1, y1, x2, y2, stroke: '#a49c90', 'stroke-width': 0.8 });
}

/**
 * Setzt die Beschriftung: die Horizontkuerzel links in der Wand, die Angaben
 * zum Substrat rechts daneben, die Humusauflage darueber. Wo jedes Stueck
 * sitzt, rechnet `beschriftungsPlan` - auch den Einzug `x` eines Kuerzels, das
 * einem anderen zur Seite ausweichen musste.
 *
 * Erst alle Fuehrungslinien, dann die Texte: Eine Linie zur Angabenspur laeuft
 * an der Kuerzelspur vorbei, und das Kuerzel soll darueber stehen, nicht
 * darunter.
 */
function beschrifte(
	svg: SVGElement, plan: BeschriftungsPlan, x0: number, wandBreite: number,
): void {
	const kante = x0 + wandBreite;
	const kuerzelX = kante + plan.kuerzelX;
	const angabenX = kante + plan.angabenX;

	// Die Zeilen der Humusauflage haengen an einer gemeinsamen Klammer: eine
	// Linie vom Block zur Klammer, und an ihr stehen Ol, Of und Oh.
	const klammer = plan.auflageKlammer;
	if (klammer) {
		const kx = kuerzelX - 6;
		svg.appendChild(fuehrung(kante, klammer.ankerY, kx, (klammer.vonY + klammer.bisY) / 2));
		svg.appendChild(fuehrung(kx, klammer.vonY, kx, klammer.bisY));
	}

	plan.kuerzel.forEach(z => {
		if (z.ohneLinie) return;
		svg.appendChild(fuehrung(kante, z.ankerY, kuerzelX - 4, z.y));
	});
	plan.angaben.forEach(z => {
		if (z.ohneLinie) return;
		svg.appendChild(fuehrung(kante - 6, z.ankerY, angabenX - 4, z.y));
	});

	plan.inDerWand.forEach(z => {
		svg.appendChild(text(x0 + z.x, z.y + 4, z.text, {
			'font-size': SCHRIFT_WAND, 'font-weight': 600, fill: SCHRIFT_FARBE, ...HALO,
		}));
	});

	plan.kuerzel.forEach(z => {
		// Der helle Grund trennt das Kuerzel von einer Fuehrungslinie, die
		// darunter durch zur Angabenspur laeuft.
		svg.appendChild(el('rect', {
			x: kuerzelX - 2, y: z.y - 4, width: textBreite(z.text, SCHRIFT_SPUR, true) + 4,
			height: 12, class: 'gb-textgrund',
		}));
		svg.appendChild(text(kuerzelX, z.y + 3.6, z.text, {
			'font-size': SCHRIFT_SPUR, 'font-weight': 600, fill: SCHRIFT_FARBE, ...HALO,
		}));
	});

	plan.angaben.forEach(z => {
		svg.appendChild(text(angabenX, z.y + 3.6, z.text, {
			'font-size': SCHRIFT_SPUR, fill: SCHRIFT_FARBE, ...HALO,
		}));
	});
}

/* --- Muster der Horizonte -------------------------------------------------- */

/** Rosttoene der Oxidationsflecken - kraeftig, wie im Gelaende. */
const ROST = ['#e07b1c', '#c96412', '#d98b2b'];

/**
 * Ein unregelmaessig begrenzter Fleck.
 *
 * Rost- und Bleichflecken sind im Boden nie kreisrund; eine Ellipse sieht
 * gezeichnet aus, ein zackiger Umriss nach Boden. Gebaut aus einem Ring von
 * Punkten mit wechselndem Abstand zur Mitte, weich verbunden.
 */
function klecks(
	ziel: SVGElement, cx: number, cy: number, groesse: number,
	farbe: string, deckung: number, r: () => number,
): void {
	const ecken = 7 + Math.floor(r() * 4);
	const breit = 0.9 + r() * 0.5;
	const punkte: [number, number][] = [];
	for (let i = 0; i < ecken; i++) {
		const w = (i / ecken) * Math.PI * 2;
		const rad = groesse * (0.55 + r() * 0.65);
		punkte.push([cx + Math.cos(w) * rad * breit, cy + Math.sin(w) * rad * 0.62]);
	}
	// Durch die Punkte eine geschlossene, weiche Linie legen.
	let d = `M${punkte[0][0].toFixed(1)} ${punkte[0][1].toFixed(1)}`;
	for (let i = 0; i < punkte.length; i++) {
		const a = punkte[i];
		const c = punkte[(i + 1) % punkte.length];
		d += ` Q${a[0].toFixed(1)} ${a[1].toFixed(1)} ${((a[0] + c[0]) / 2).toFixed(1)} ${((a[1] + c[1]) / 2).toFixed(1)}`;
	}
	ziel.appendChild(el('path', { d: d + 'Z', fill: farbe, opacity: deckung }));
}

function muster(
	g: SVGElement, defs: SVGElement, art: Muster | undefined,
	x: number, y: number, b: number, h: number, seed: number, staerke = 1,
	baender?: { lage: WechselLage2; von: number; bis: number; grundmasse?: boolean }[] | null,
	baenderDichte = 1,
): void {
	if (!art || art === 'keins') return;
	const r = streu(seed * 7919 + 13);
	const clipId = `gb-c${seed}-${Math.round(y)}`;
	defs.appendChild(el('clipPath', { id: clipId }, [el('rect', { x, y, width: b, height: h })]));
	const inner = el('g', { 'clip-path': `url(#${clipId})` });
	const flaeche = b * h;

	if (art === 'rost' || art === 'rost-wenig') {
		// Rostflecken sind im Gelaende kraeftig orange und unregelmaessig
		// begrenzt, nicht blass und rund. Sie sind das Erkennungszeichen des
		// Go-Horizontes - wer sie nur ahnt, sieht den Horizont nicht.
		const n = Math.round(flaeche / (art === 'rost' ? 900 : 2200) * staerke);
		for (let i = 0; i < n; i++) {
			klecks(inner, x + r() * b, y + r() * h, 3.5 + r() * 6, ROST[Math.floor(r() * ROST.length)],
				(0.45 + r() * 0.35) * staerke, r);
		}
	} else if (art === 'bleich') {
		// Sw: vorherrschend grau mit Bleich- UND Rostflecken (SEA). Die hellen
		// Flecken muessen als eigene Form erkennbar sein.
		const n = Math.round(flaeche / 1000 * staerke);
		for (let i = 0; i < n; i++) {
			const hell = r() > 0.45;
			klecks(inner, x + r() * b, y + r() * h, hell ? 4 + r() * 6 : 3 + r() * 4.5,
				hell ? '#eceff1' : ROST[Math.floor(r() * ROST.length)],
				(hell ? 0.62 : 0.5) * staerke, r);
		}
	} else if (art === 'marmor') {
		// Sd: grau und rostfarben marmoriert, dichter als Sw (SEA).
		const n = Math.round(flaeche / 700 * staerke);
		for (let i = 0; i < n; i++) {
			const rost = r() > 0.55;
			klecks(inner, x + r() * b, y + r() * h, 3 + r() * 6,
				rost ? ROST[Math.floor(r() * ROST.length)] : '#dfe2e4',
				(rost ? 0.45 : 0.5) * staerke, r);
		}
	} else if (art === 'baender') {
		if (baender && baender.length) {
			// Aus der Substratfolge abgeleitet: Jedes Band steht fuer eines der
			// Substrate, die laut Wechsel-Haken hier miteinander wechseln, und ist
			// so dick wie sein Mengenanteil. Grobe Koernung wird dunkler und
			// kraeftiger gezeichnet als feine - so sieht man, WAS hier wechselt.
			//
			// Die Grundmasse (die Lage mit dem groessten Anteil) tritt umso
			// weiter zurueck, je weniger Baender der Horizont haben soll: Beim
			// Bb bleibt sie fast so kraeftig wie die Baender, beim Bb↔C
			// verschwindet sie, und es bleiben wenige dunkle Baender auf hellem
			// Grund (Nutzer-Vorgabe 21.09.2026).
			const grundGewicht = Math.max(0, Math.min(1, (baenderDichte - 0.25) / 0.75));
			for (const band of baender) {
				const gewicht = band.grundmasse ? grundGewicht : 1;
				if (gewicht <= 0) continue;
				const dicke = Math.max(1, band.bis - band.von);
				const mitte = band.von + dicke / 2;
				const dunkel = (0.24 + band.lage.korn * 0.055) * gewicht;
				let d = `M${x} ${mitte.toFixed(1)}`;
				for (let p = x; p <= x + b; p += 18) {
					d += ` q 9 ${((r() - 0.5) * 4).toFixed(1)} 18 ${((r() - 0.5) * 2.5).toFixed(1)}`;
				}
				inner.appendChild(el('path', {
					d, fill: 'none', stroke: '#7a4f2c', 'stroke-width': dicke,
					opacity: Math.min(0.62, dunkel) * staerke, 'stroke-linecap': 'round',
				}));
			}
		} else {
			// Ohne Wechselangabe in der Substratfolge bleibt es bei einer
			// allgemeinen Baenderung: Dass es Baender gibt, sagt der Horizont -
			// wie viele und wie dick, sagt nur die Substratfolge.
			//
			// Wie dicht sie liegen, folgt der Baenderstufe: Ein Bb↔C bekommt
			// wenige, ein Bb die meisten. Dunkel bleiben sie in jedem Fall - es
			// werden weniger, nicht blassere.
			const abstand = 1 / Math.max(0.1, baenderDichte);
			let cy = y + (4 + r() * 8) * abstand;
			while (cy < y + h) {
				let d = `M${x} ${cy}`;
				for (let p = x; p <= x + b; p += 16) d += ` q 8 ${(r() - 0.5) * 5} 16 ${(r() - 0.5) * 3}`;
				inner.appendChild(el('path', {
					d, fill: 'none', stroke: '#7a4f2c', 'stroke-width': 1.6 + r() * 3.4,
					opacity: (0.3 + r() * 0.25) * staerke, 'stroke-linecap': 'round',
				}));
				cy += (7 + r() * 13) * abstand;
			}
		}
	} else if (art === 'streu' || art === 'fasern') {
		const n = Math.round(flaeche / (art === 'streu' ? 140 : 190));
		for (let i = 0; i < n; i++) {
			const cx = x + r() * b, cy = y + r() * h, lang = 4 + r() * 9;
			const dreh = art === 'streu' ? -40 + r() * 80 : -12 + r() * 24;
			inner.appendChild(el('line', {
				x1: cx - lang / 2, y1: cy, x2: cx + lang / 2, y2: cy,
				stroke: '#d9c49a', 'stroke-width': 0.9, opacity: 0.3 + r() * 0.3,
				transform: `rotate(${dreh} ${cx} ${cy})`,
			}));
		}
	} else if (art === 'broeckel') {
		const n = Math.round(flaeche / 500);
		for (let i = 0; i < n; i++) {
			const cx = x + r() * b, cy = y + r() * h, s = 4 + r() * 6;
			inner.appendChild(el('rect', {
				x: cx, y: cy, width: s, height: s * 0.8, rx: 1.5, fill: 'none',
				stroke: '#cbb894', 'stroke-width': 0.8, opacity: 0.3,
				transform: `rotate(${r() * 40 - 20} ${cx} ${cy})`,
			}));
		}
	}
	// 'wurzeln' wird hier NICHT gezeichnet: Wurzeln laufen ueber Schichtgrenzen
	// hinweg und werden deshalb einmal fuer den ganzen durchwurzelten Bereich
	// gezeichnet (siehe `wurzeln` in zeichneGrubenbild). Je Abschnitt gezeichnet
	// haetten sie an jeder Grenze neu angefangen (Nutzer-Befund 19.09.2026).
	g.appendChild(inner);
}

/**
 * Bis zu welcher Tiefe durchwurzelt gezeichnet wird, in cm.
 *
 * Das ist die Untergrenze des letzten Abschnitts von oben, dessen Horizont als
 * durchwurzelt gilt (A-Horizonte). Bricht die Reihe ab - etwa weil unter dem Ah
 * ein Bv folgt -, hoeren die Wurzeln dort auf.
 */
function durchwurzeltBis(abs: Abschnitt[], farben: { muster: Muster }[]): number {
	let bis = 0;
	for (let i = 0; i < abs.length; i++) {
		if (farben[i]?.muster !== 'wurzeln') break;
		bis = abs[i].bis;
	}
	return bis;
}

/** So tief reicht eine Baumwurzel im Bild, wenn nichts sie vorher aufhaelt. */
export const BAUMWURZEL_CM = 75;

/** Staendig nasse Horizonte - dort endet die Wurzel. */
const NASSE_HORIZONTE = new Set(['Gr', 'Hm']);

/**
 * Bis wohin die Baumwurzel gezeichnet wird, in cm.
 *
 * Typisch 50 cm bis 1 m, aber NICHT ins Wasser (Nutzer-Vorgabe 19.09.2026):
 * Unter dem Wasserspiegel bekommt eine Wurzel keine Luft. Aufgehalten wird sie
 * vom gemessenen Wasserstand und, wo der fehlt, von der Oberkante des ersten
 * staendig nassen Horizontes.
 *
 * Ein RELIKTISCHER Grundgley (`Gr r`) zaehlt NICHT als nass: Das nachgestellte
 * „r" sagt gerade, dass dort heute kein Wasser mehr steht.
 */
function baumwurzelBis(
	abs: Abschnitt[], gesamtTiefe: number, spiegelTiefe?: number | null,
): number {
	let bis = Math.min(BAUMWURZEL_CM, gesamtTiefe);
	const spiegel = zahl(spiegelTiefe);
	if (spiegel > 0) bis = Math.min(bis, spiegel);
	const nass = ersteNasseTiefe(abs);
	if (nass !== null) bis = Math.min(bis, nass);
	return Math.max(0, bis);
}

/** Obergrenze des ersten staendig nassen Horizontes, sonst `null`. */
function ersteNasseTiefe(abs: Abschnitt[]): number | null {
	for (const a of abs) {
		for (const roh of a.horizont?.horizonte ?? []) {
			const g = leseSymbol(roh);
			if (NASSE_HORIZONTE.has(g.kern) && !g.reliktisch) return a.von;
		}
	}
	return null;
}

const WASSER_FARBE = '#2f88a8';
const WASSER_LINIE = '#15718f';

/**
 * Eine starke Wurzel ist warm hellbraun (Rinde), eine Feinwurzel fast weiss.
 * Eine einzige helle Farbe fuer beides leuchtete auf dem dunklen Ah wie ein
 * Lichtstrahl und verschwand zugleich im hellen C (Befund am Bild 19.09.2026).
 */
const WURZEL_FARBE = '#d2bc95';
const WURZEL_FEIN = '#dbcdab';
const WURZEL_RAND = '#7d6b4a';

/**
 * So durchsichtig ist das ganze Wurzelwerk - an der GRUPPE, nicht am einzelnen
 * Strang.
 *
 * Jeden Strang fuer sich durchsichtig zu machen war der Fehler: Wo zwei
 * Straenge uebereinanderliegen, addieren sich zwei halbdurchsichtige Flaechen
 * zu einer dichteren, und man sieht jede Naht - „nicht gewachsen, sondern
 * rangestueckelt" (Nutzer-Befund 19.09.2026). Deshalb sind die Teile voll
 * deckend und nur die Gruppe ist durchsichtig.
 *
 * Zart gehalten (Nutzer-Wunsch 20.09.2026): Die Wurzel gehoert ins Bild, sie
 * soll es aber nicht beherrschen - der Boden ist die Aussage.
 */
const WURZEL_DECKUNG = 0.3;

/**
 * Duenner als ein Bildpunkt wird nicht gezeichnet.
 *
 * Eine Kraeuterwurzel ist 1 bis 2 mm stark; bei 2 px/cm waere das ein halber
 * Bildpunkt, und sie verschwaende ganz. Das Bild ist ein Schema - hier darf
 * uebertrieben werden, damit die Aussage „durchwurzelt" ueberhaupt ankommt.
 *
 * ⚠ Die Grenze liegt tief (0,45), weil sie sonst die Unterschiede frisst:
 * Lag sie bei 0,9, kamen ALLE feinen Straenge gleich dick heraus, und aus dem
 * Geflecht wurde Filz (Nutzer-Befund 20.09.2026). Verschiedene Staerken sind
 * wichtiger als die letzte Sichtbarkeit des duennsten Haars.
 */
function mindestens(v: number): number {
	return Math.max(0.45, v);
}

/* --- Der Wurzelweg -------------------------------------------------------- */

/** Ein Stuetzpunkt auf dem Wurzelweg: Ort und Laufrichtung. */
interface Stelle { x: number; y: number; winkel: number; }

/**
 * Ein WURZELWEG: die fertige Mittellinie eines Stranges.
 *
 * ⚠ Aus ihm wird gezeichnet UND an ihm wird angesetzt. Lag die Schlaengelung
 * erst im Zeichnen, setzte ein Abzweig an der glatten Rechenkurve an, waehrend
 * die Mutterwurzel daneben gezeichnet wurde - Wurzeln hingen frei in der Luft
 * (Nutzer-Befund 20.09.2026).
 */
interface Weg { stellen: Stelle[]; }

/**
 * ⚠ Winkel in SVG: 0 zeigt nach RECHTS, 90 nach UNTEN, 180 nach LINKS.
 *
 * Eine Wurzel waechst nie nach oben. Alles zwischen 12 und 168 Grad ist
 * erlaubt (von fast waagerecht rechts ueber senkrecht nach unten bis fast
 * waagerecht links); alles ausserhalb wird hierher zurueckgeholt. Ohne diese
 * Klammer liefen Abzweige nach oben davon, und aus den Kraeuterwurzeln wurden
 * Schneeflocken (Nutzer-Befund 20.09.2026).
 */
function abwaerts(winkel: number, engstens = 12): number {
	return Math.max(engstens, Math.min(180 - engstens, winkel));
}

/**
 * Baut den Weg eines Stranges: von `winkelVon` am Ansatz nach `winkelBis` an
 * der Spitze, gleichmaessig gebogen und mit einer ruhigen Schlaengelung.
 *
 * Die stetige Biegung ist das Wichtigste am Bild: Eine Wurzel zieht nicht
 * schnurgerade, sie holt aus und legt sich zur Spitze hin nach unten. Die
 * Schlaengelung beginnt am Ansatz bei null, damit ein Abzweig genau dort
 * sitzt, wo er angesetzt hat.
 */
function wegBauen(
	x: number, y: number, winkelVon: number, winkelBis: number, laenge: number,
	r: () => number,
): Weg {
	const N = 22;
	// Zwei flache Wellen - eine Wurzel wandert, sie zappelt nicht.
	const a1 = 0.6 + r() * 0.5, p1 = r() * Math.PI * 2, f1 = 0.6 + r() * 0.7;
	const a2 = 0.2 + r() * 0.25, p2 = r() * Math.PI * 2, f2 = 1.4 + r() * 1.0;
	const welle = (t: number) =>
		a1 * Math.sin(p1 + t * Math.PI * f1) + a2 * Math.sin(p2 + t * Math.PI * f2);
	const ausschlag = laenge * (0.015 + r() * 0.025);

	const stellen: Stelle[] = [];
	let px = x, py = y;
	for (let i = 0; i <= N; i++) {
		const t = i / N;
		const grad = winkelVon + (winkelBis - winkelVon) * t;
		const w = (grad * Math.PI) / 180;
		if (i > 0) {
			px += Math.cos(w) * (laenge / N);
			py += Math.sin(w) * (laenge / N);
		}
		const quer = ausschlag * welle(t) * t;
		stellen.push({ x: px - Math.sin(w) * quer, y: py + Math.cos(w) * quer, winkel: 0 });
	}
	for (let i = 0; i <= N; i++) {
		const a = stellen[Math.max(0, i - 1)], b = stellen[Math.min(N, i + 1)];
		stellen[i].winkel = (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
	}
	return { stellen };
}

/** Die Stelle bei `t` (0 = Ansatz, 1 = Spitze). */
function stelleAuf(w: Weg, t: number): Stelle {
	const n = w.stellen.length - 1;
	return w.stellen[Math.max(0, Math.min(n, Math.round(t * n)))];
}

/**
 * Wie dick ein Strang an der Stelle `t` ist.
 *
 * `(1 - t)` hoch `SPITZ_ZU` - der Strang bleibt zunaechst kraeftig und laeuft
 * erst gegen Ende schnell aus. Ein Kegel (Hoch 1) sieht aus wie eine Moehre,
 * eine gleichbleibende Breite wie ein Balken; beides stand schon im Bild.
 */
const SPITZ_ZU = 1.7;

function dickeBei(d0: number, d1: number, t: number): number {
	return d1 + (d0 - d1) * Math.pow(1 - t, SPITZ_ZU);
}

/**
 * Der Umriss eines Stranges: ein gefuelltes Band entlang seines Weges,
 * von `d0` am Ansatz auf `d1` an der Spitze.
 *
 * Die Dicke schwankt leicht - eine Wurzel hat Verdickungen und
 * Einschnuerungen. Am Ansatz ist die Schwankung null, damit die
 * Anschlussstelle sauber bleibt.
 */
function band(weg: Weg, d0: number, d1: number, r: () => number): string {
	const a1 = 0.5 + r() * 0.5, p1 = r() * Math.PI * 2, f1 = 1.2 + r() * 1.4;
	const n = weg.stellen.length - 1;
	const links: string[] = [];
	const rechts: string[] = [];
	for (let i = 0; i <= n; i++) {
		const t = i / n;
		const p = weg.stellen[i];
		const schwankung = 1 + 0.14 * a1 * Math.sin(p1 + t * Math.PI * f1) * t;
		// ⚠ Auf den letzten Prozenten gilt die Darstellungsgrenze NICHT.
		// Sonst endet jede Wurzel mit einem halben Bildpunkt Breite, und das
		// sieht abgeschnitten aus (Nutzer-Befund 20.09.2026) - gerade dort,
		// wo ein Strang an der Wurzeltiefe gekuerzt wurde. Laeuft der Strang
		// gar nicht spitz aus (`d1 > 0`, etwa der Wurzelanlauf, der seitlich
		// aus dem Bild geht), bleibt er am Ende voll.
		const spitz = d1 === 0 ? Math.min(1, (1 - t) / 0.16) : 1;
		const halb = (mindestens(dickeBei(d0, d1, t) * schwankung) * spitz) / 2;
		const nx = -Math.sin((p.winkel * Math.PI) / 180);
		const ny = Math.cos((p.winkel * Math.PI) / 180);
		links.push(`${(p.x + nx * halb).toFixed(1)} ${(p.y + ny * halb).toFixed(1)}`);
		rechts.push(`${(p.x - nx * halb).toFixed(1)} ${(p.y - ny * halb).toFixed(1)}`);
	}
	return `M${links.join('L')}L${rechts.reverse().join('L')}Z`;
}

/* --- Die Wurzel ----------------------------------------------------------- */

/** Sammelt die Umrisse, bis am Ende zwei Flaechen daraus werden. */
interface Wurzelsammlung { grob: string[]; fein: string[]; }

/**
 * Bis hierher zaehlt der humose Oberboden fuer die angeschnittenen Wurzeln.
 *
 * Kraeuterwurzeln wurden am 20.09.2026 auf Nutzer-Entscheidung wieder
 * weggelassen: Sie liessen sich nicht so zeichnen, dass sie neben der
 * Baumwurzel bestehen - entweder wurden sie zum Knaeuel oder zum kahlen
 * Strich. Die Tiefe steuert jetzt nur noch, wie weit oben die angeschnittenen
 * Wurzeln liegen.
 */
const KRAUT_TIEFE_HOECHSTENS = 20;

/**
 * Wie ein Wurzelwerk waechst.
 *
 * ⚠ Baum und Kraut benutzen DENSELBEN Bauplan. Sie mit zwei verschiedenen
 * Verfahren zu zeichnen war ein Denkfehler (Nutzer-Frage 20.09.2026: „warum
 * glaubst du, dass eine Baumwurzel anders aussehen muesste?"). Eine Wurzel ist
 * eine Wurzel - sie gabelt sich, wieder und wieder, und wird dabei duenner.
 * Verschieden sind nur die Zahlen: Der Baum ist dicker, tiefer und darf
 * flacher wegstreben, das Kraut bleibt duenn, flach und steil.
 */
interface Gabelung {
	s: Wurzelsammlung;
	ziel: 'grob' | 'fein';
	/** Bis hierher darf gewurzelt werden. */
	untenY: number;
	px: number;
	r: () => number;
	/** So viele Gabelungen tief. */
	ordnungen: number;
	/** Nie flacher als das - eine Wurzel waechst nie nach oben. */
	steilstens: number;
	/** Wie viel Dicke und Laenge die Fortsetzung der Achse behaelt. */
	achseDicke: number;
	achseLaenge: number;
	/** Wie weit eine Seitengabel abweicht, in Grad. */
	seitenWinkel: [number, number];
	/** Duenner als das wird nicht mehr gezeichnet. */
	duennstens: number;
	/**
	 * Wie stark die Achse zur SENKRECHTEN zurueckgeholt wird (0 bis 1).
	 *
	 * Ohne das ist die Richtung ein Zufallsweg: Jeder Abschnitt dreht um bis
	 * zu 16 Grad, und ueber sieben Ordnungen summiert sich das - die Wurzel
	 * kippte fast immer zu einer Seite weg (Nutzer-Befund 20.09.2026: „warum
	 * so oft so einseitig?"). Der Regelfall ist eine gleichmaessige
	 * senkrechte Wurzel; krumm und einseitig ist die Ausnahme.
	 */
	lotrecht: number;
	/** Alle entstandenen Wege - fuer die Pruefung, was sich ueberlappt. */
	wege: Weg[];
}

/**
 * Wie oft laenger als dick ein Strang mindestens ist.
 *
 * ⚠ Nicht groesser waehlen. Bei 6 verwarf die Regel den STAMM, sobald das Bild
 * schmal war - der erste Abschnitt ist nur gut ein Drittel der Wurzeltiefe
 * lang, und ein 5 cm starker Stamm braucht dann mehr Platz, als da ist
 * (Test `wurzeln.spec.ts` wurde rot). 2,5 genuegt, um die kurzen dicken Keile
 * zu verhindern, um die es ging.
 */
const MINDESTENS_SO_LANG = 2.5;

/**
 * Ein Wurzelstrang und seine Gabelungen - rekursiv.
 *
 * Aus jedem Strang wird die ACHSE fortgesetzt (kleiner Winkel, fast die ganze
 * Dicke) und ein bis zwei SEITENGABELN abgezweigt (groesserer Winkel,
 * deutlich duenner). Daher hat das Ergebnis einen erkennbaren Hauptstrang und
 * trotzdem ein feines Geflecht - dieselbe Gestalt wie auf einem Foto einer
 * ausgewaschenen Wurzel.
 */
function gabel(
	g: Gabelung, x: number, y: number, richtung: number, laenge: number,
	dicke: number, ordnung: number,
): boolean {
	const r = g.r;
	if (ordnung > g.ordnungen || laenge < g.px * 0.8) return false;
	if (dicke < g.px * g.duennstens * 0.9) return false;

	const bis = abwaerts(richtung + (r() * 2 - 1) * 16, g.steilstens);

	// ⚠ NICHT abschneiden, sondern von vornherein kuerzer wachsen lassen.
	// Wer einen Strang an der Wurzeltiefe kappt, laesst ihm fuer die Spitze
	// kaum Laenge - das wird eine Meisselkante statt einer Spitze (Nutzer-
	// Befund 20.09.2026, mehrfach gemeldet). Hier wird ausgerechnet, wie viel
	// Platz nach unten noch ist, und der Strang nimmt sich davon nur einen
	// Teil. Der Rest ist Luft fuer die Verjuengung, und weil der Anteil
	// streut, enden die Straenge in verschiedenen Tiefen statt auf einer Linie.
	const faellt = Math.max(0.15, Math.sin((((richtung + bis) / 2) * Math.PI) / 180));
	const platz = (g.untenY - y) / faellt;
	if (platz < g.px * 1.2) return false;
	const echt = Math.min(laenge, platz * (0.55 + r() * 0.4));
	// ⚠ Eine Wurzel ist nie kuerzer als ein Vielfaches ihrer Dicke. Ohne diese
	// Bedingung entstand aus einem kurzen, dicken Abzweig ein KEIL, der wie
	// ein loses Stueck im Boden lag.
	if (echt < g.px * 0.8 || echt < dicke * MINDESTENS_SO_LANG) return false;

	const weg = wegBauen(x, y, richtung, bis, echt, r);
	const ende = weg.stellen[weg.stellen.length - 1];

	// ⚠ ERST die Fortsetzung bauen, DANN entscheiden, wie dieser Abschnitt
	// endet.
	//
	// Umgekehrt war es der Fehler: Ich habe VORHERGESAGT, dass die Achse
	// weitergeht, und den Abschnitt darum dick enden lassen. Scheiterte die
	// Fortsetzung dann an einer ihrer eigenen Bedingungen (kein Platz mehr, zu
	// kurz, zu duenn), blieb genau dort ein STUMPF stehen - unten im Bild, wo
	// es am meisten auffaellt (Nutzer-Befund 20.09.2026: „darf niemals in
	// einem Stumpf enden"). Jetzt sagt die Fortsetzung selbst, ob es sie gibt;
	// gibt es sie nicht, laeuft der Abschnitt spitz aus.
	//
	// Der Platzhalter haelt die Reihenfolge: Der Abschnitt steht in der Liste
	// vor seinen Abzweigen, obwohl er erst nach ihnen fertig gerechnet ist.
	const stelle = g.s[g.ziel].push('') - 1;
	g.wege.push(weg);

	const naechste = dicke * g.achseDicke * (0.9 + r() * 0.2);
	// Die Achse dreht ein Stueck, wird aber zur Senkrechten zurueckgeholt.
	const gedreht = ende.winkel + (r() * 2 - 1) * 14;
	const weiter = gabel(g, ende.x, ende.y,
		abwaerts(gedreht + (90 - gedreht) * g.lotrecht, g.steilstens),
		echt * g.achseLaenge * (0.85 + r() * 0.3), naechste, ordnung + 1);
	const dEnde = weiter ? naechste : 0;
	g.s[g.ziel][stelle] = band(weg, dicke, dEnde, r);

	// Ein bis zwei Seitengabeln, deutlich duenner als die Achse an dieser
	// Stelle.
	const [wMin, wMax] = g.seitenWinkel;
	const zahl = 1 + (r() > 0.35 ? 1 : 0);
	// Die Seiten WECHSELN sich ab, statt jedes Mal neu ausgelost zu werden -
	// sonst landen mehrere Abzweige zufaellig auf derselben Seite, und der
	// Strang sieht einseitig aus. Welche Seite den Anfang macht, ist frei.
	const erste = r() > 0.5 ? 1 : -1;
	for (let k = 0; k < zahl; k++) {
		// Ab dem Ansatz, nicht erst ab einem Drittel: Eine Wurzel zweigt
		// gleich unter der Oberflaeche ab (Nutzer-Vorgabe 20.09.2026).
		const t = 0.1 + (k + r() * 0.8) * (0.85 / zahl);
		const pp = stelleAuf(weg, t);
		const ab = k % 2 === 0 ? erste : -erste;
		gabel(g, pp.x, pp.y,
			abwaerts(pp.winkel + ab * (wMin + r() * (wMax - wMin)), g.steilstens),
			echt * (0.45 + r() * 0.35),
			dickeBei(dicke, dEnde, t) * (0.4 + r() * 0.22), ordnung + 1);
	}
	return true;
}

/**
 * Wurzeln: EINE Baumwurzel, dazu ein paar Kraeuterwurzeln.
 *
 * Gerechnet wird in ZENTIMETERN: Wie dick eine Wurzel ist, haengt am Massstab
 * des Bildes, nicht an seiner Groesse auf dem Schirm. Zur Einordnung
 * (Nutzer 19.09.2026): Bei 1,50 m Wandbreite hat ein Baumfuss gut 50 cm.
 *
 * Der Zufall `r` kommt vom PUNKT (siehe `saat`), nicht aus einer festen Zahl.
 */
function wurzeln(
	ziel: SVGElement, x: number, oben: number, b: number, px: number,
	baumTiefeCm: number, krautTiefeCm: number, auflageCm: number, r: () => number,
): void {
	const cm = (v: number) => v * px;
	const baumH = cm(baumTiefeCm);
	const krautH = cm(Math.min(krautTiefeCm, KRAUT_TIEFE_HOECHSTENS)) + cm(auflageCm);
	// Oberkante der Humusauflage - dort setzen die Wurzeln an, nicht erst an
	// der Gelaendeoberflaeche.
	const filz = oben - cm(auflageCm);
	const s: Wurzelsammlung = { grob: [], fein: [] };

	// Angeschnittene zuerst: Sie liegen hinter dem, was der Laenge nach zu
	// sehen ist.
	const quer = querWurzeln(x, oben, b, krautH, px, r);

	const seite = r() > 0.5 ? 1 : -1;
	const sx = x + b * (0.28 + r() * 0.44);
	const stammDicke = cm(3.5 + r() * 2);
	// Der Regelfall ist eine gleichmaessige SENKRECHTE Wurzel; etwa jede
	// vierte darf krumm, einseitig und tiefer verzweigt sein (Nutzer-Vorgabe
	// 20.09.2026).
	const krumm = r() > 0.75;
	const baum: Gabelung = {
		s, ziel: 'grob', untenY: oben + baumH, px, r,
		// Viele Ordnungen: Die Wurzel teilt sich weiter, bis sie ganz fein
		// ausgelaufen ist (Nutzer-Wunsch 20.09.2026). Was zu kurz oder zu
		// duenn waere, faellt von selbst weg.
		//
		// ⚠ `duennstens` NICHT tiefer setzen. Unterhalb von `mindestens`
		// (0,45 Bildpunkte) wird jeder Strang gleich breit gezeichnet - noch
		// feinere Ordnungen kosten also Rechenzeit, ohne etwas zu zeigen. Mit
		// 0,07 waren es gemessen 1500 bis 1800 Straenge und fast 1 MB
		// Pfaddaten je Bild; das Bild wird bei jedem Klick neu gezeichnet.
		ordnungen: krumm ? 8 : 7, steilstens: 24,
		achseDicke: 0.84, achseLaenge: 0.76,
		seitenWinkel: [26, 58], duennstens: 0.16,
		lotrecht: krumm ? 0.05 : 0.45, wege: [],
	};

	// Der Hauptstrang: von der Oberkante der Auflage schraeg in die Tiefe.
	gabel(baum, sx, filz - cm(1),
		abwaerts(90 - seite * (krumm ? 12 + r() * 16 : r() * 6), 24),
		baumH * 0.42 + cm(auflageCm), stammDicke, 1);

	// Der Wurzelanlauf: fast waagerecht dicht unter der Oberflaeche.
	//
	// Es gibt ihn NICHT in jedem Bild (Nutzer-Befund 20.09.2026: „scheint ein
	// fixes Muster zu sein"). Eine Grube trifft den Wurzelteller eines Baumes
	// eben mal und mal nicht.
	if (r() > 0.45 && baum.wege.length) {
		const flach: Gabelung = { ...baum, steilstens: 8, seitenWinkel: [20, 45] };
		// ⚠ Er setzt auf dem WEG des Stammes an, nicht auf dessen Startpunkt.
		// Der Stamm faellt schraeg ein; ein paar Zentimeter tiefer steht er
		// schon woanders, und der Anlauf begann daneben in der Luft
		// (Nutzer-Befund 20.09.2026).
		const p = stelleAuf(baum.wege[0], 0.06 + r() * 0.14);
		gabel(flach, p.x, p.y, seite > 0 ? 168 - r() * 10 : 12 + r() * 10,
			b * (0.3 + r() * 0.3), stammDicke * (0.5 + r() * 0.2), 2);
	}

	// Alles in EINE Gruppe, die Teile darin voll deckend. Zwei Flaechen, weil
	// eine Feinwurzel heller ist als eine Grobwurzel; die feinen liegen oben.
	const g = el('g', { opacity: WURZEL_DECKUNG });
	quer.forEach(e => g.appendChild(e));
	g.appendChild(el('path', { d: s.grob.join(' '), fill: WURZEL_FARBE }));
	if (s.fein.length) g.appendChild(el('path', { d: s.fein.join(' '), fill: WURZEL_FEIN }));
	ziel.appendChild(g);
}

/**
 * Quer angeschnittene Wurzeln: helle Ovale mit feinem dunklem Rand.
 *
 * In einer Grubenwand sieht man die meisten Wurzeln im Schnitt, nicht der
 * Laenge nach. Der Rand ist noetig, damit sie nicht mit den hellen Sprenkeln
 * des Karbonatgehalts oder mit einem Sandkorn verwechselt werden.
 */
function querWurzeln(
	x: number, oben: number, b: number, h: number, px: number, r: () => number,
): SVGElement[] {
	const flaecheCm2 = (b / px) * (h / px);
	const zahl = Math.max(3, Math.round(flaecheCm2 / 240));
	const aus: SVGElement[] = [];
	for (let i = 0; i < zahl; i++) {
		const cx = x + r() * b;
		// Hoch zwei: oben dicht, nach unten weniger.
		const cy = oben + h * r() * r();
		// Wenige staerkere, viele feine - dieselbe Verteilung wie der Laenge nach.
		const dickCm = r() > 0.82 ? 1 + r() * 1.6 : 0.25 + r() * 0.45;
		const rx = mindestens((dickCm / 2) * px);
		// Quer, schraeg oder fast laengs angeschnitten: Ein Schnitt trifft eine
		// Wurzel selten im rechten Winkel, und dann ist ihr Bild langgezogen.
		const laengs = r() > 0.7 ? 1.6 + r() * 1.8 : 0.62 + r() * 0.4;
		aus.push(el('ellipse', {
			cx, cy, rx, ry: rx * laengs,
			fill: WURZEL_FEIN,
			stroke: WURZEL_RAND, 'stroke-width': Math.max(0.2, px * 0.05),
			'stroke-opacity': 0.4,
			transform: `rotate(${(r() * 2 - 1) * 55} ${cx} ${cy})`,
		}));
	}
	return aus;
}

/* --- Textur aus den Substrateigenschaften ---------------------------------- */

/**
 * Die Substrateigenschaften legen sich UEBER die Horizontfarbe:
 *  - Koernung: Sand wird gekoernt gezeichnet, grob groeber als fein; bindige
 *    Substrate bekommen statt Koernern ein Gefuege aus feinen Strichen.
 *  - Skelettgehalt: Kiesel, ihre Zahl aus dem mittleren Volumenanteil der Stufe.
 *  - Karbonatgehalt: helle Sprenkel.
 *  - Wechselschichtung: feine waagerechte Baenderung.
 *
 * Der Humusgehalt steckt NICHT hier, sondern in der Grundfarbe (schichtFarbe):
 * er ueberpraegt den Horizont, er liegt nicht als Schleier darueber.
 */
function textur(
	g: SVGElement, defs: SVGElement, z: SubstratZeile | null,
	x: number, y: number, b: number, h: number, seed: number, px: number,
	schonGebaendert = false,
): SVGElement | null {
	if (!z) return null;
	const art = bodenart(z.substrat);
	const r = streu(seed * 104729 + 7);
	const clipId = `gb-s${seed}-${Math.round(y)}`;
	defs.appendChild(el('clipPath', { id: clipId }, [el('rect', { x, y, width: b, height: h })]));
	const inner = el('g', { 'clip-path': `url(#${clipId})` });
	const flaeche = b * h;
	// Bei kleinem Maszstab werden Koerner sonst zu Matsch.
	const fein = px / 3;

	if (art?.gruppe === 'sand') {
		const punkt = (0.5 + art.korn * 0.42) * Math.max(0.7, fein);
		const dichte = 46 + art.korn * 22;
		const n = Math.min(2400, Math.round(flaeche / dichte));
		for (let i = 0; i < n; i++) {
			inner.appendChild(el('circle', {
				cx: x + r() * b, cy: y + r() * h, r: punkt * (0.65 + r() * 0.7),
				fill: r() > 0.5 ? '#3a2e1e' : '#6b5a3e', opacity: 0.1 + r() * 0.16,
			}));
		}
		if (art.bindig > 0) {
			const m = Math.round(flaeche / (360 / art.bindig));
			for (let i = 0; i < m; i++) {
				const cx = x + r() * b, cy = y + r() * h, lang = 5 + r() * 9;
				inner.appendChild(el('line', {
					x1: cx - lang / 2, y1: cy, x2: cx + lang / 2, y2: cy,
					stroke: '#4d3d28', 'stroke-width': 0.7, opacity: 0.1 + art.bindig * 0.035,
					transform: `rotate(${-8 + r() * 16} ${cx} ${cy})`,
				}));
			}
		}
	} else if (art?.gruppe === 'bindig') {
		// Die uebliche Signatur fuer bindiges, dichtes Material sind WAAGERECHTE
		// Strichbuendel (siehe Legenden bodenkundlicher Profildarstellungen), nicht
		// wirr liegende Striche: Sie zeigen die Schichtung und das dichte Gefuege.
		// Je bindiger, desto mehr Striche je Buendel und desto dichter die Lage.
		const buendel = Math.max(2, Math.round(h / (26 - art.bindig * 2.4)));
		for (let i = 0; i < buendel; i++) {
			const cy = y + (i + 0.5) * (h / buendel) + (r() - 0.5) * 4;
			const striche = 2 + Math.floor(art.bindig / 2) + (r() > 0.6 ? 1 : 0);
			for (let k = 0; k < striche; k++) {
				const yy = cy + k * 2.6;
				if (yy < y || yy > y + h) continue;
				const lang = b * (0.28 + r() * 0.5);
				const x1 = x + r() * (b - lang);
				inner.appendChild(el('line', {
					x1, y1: yy, x2: x1 + lang, y2: yy,
					stroke: '#4a3a26', 'stroke-width': 0.8,
					opacity: 0.16 + art.bindig * 0.035 + r() * 0.1,
				}));
			}
		}
	} else if (art?.gruppe === 'organisch') {
		const n = Math.round(flaeche / 150);
		for (let i = 0; i < n; i++) {
			const cx = x + r() * b, cy = y + r() * h, lang = 5 + r() * 10;
			inner.appendChild(el('line', {
				x1: cx - lang / 2, y1: cy, x2: cx + lang / 2, y2: cy,
				stroke: '#c2a674', 'stroke-width': 0.9, opacity: 0.2 + r() * 0.25,
				transform: `rotate(${-14 + r() * 28} ${cx} ${cy})`,
			}));
		}
	}

	if (zahl(z.karbonatgehalt) > 0) {
		const n = Math.round(flaeche / (1400 / zahl(z.karbonatgehalt)));
		for (let i = 0; i < n; i++) {
			inner.appendChild(el('circle', {
				cx: x + r() * b, cy: y + r() * h, r: 0.9 + r() * 1.6,
				fill: '#fdfbf4', opacity: 0.45 + r() * 0.3,
			}));
		}
	}

	// Stein- und Kiesgehalt werden NICHT addiert: der Skelettgehalt fasst beide
	// bereits zusammen (SEA: „sk" = skeletthaltig).
	//
	// Die Steine kommen NICHT in die Schicht, sondern in eine eigene Gruppe,
	// die der Aufrufer nach den weichen Uebergaengen zeichnet (Nutzer-Befund
	// 21.09.2026: „die Steine werden im Uebergangsbereich blasser"). Ein Stein
	// ist kein Farbton der Schicht, er verlaeuft nicht mit ihr.
	const skelett = Math.max(zahl(z.skelettgehalt), zahl(z.kiesgehalt), zahl(z.steingehalt));
	let steinLage: SVGElement | null = null;
	if (skelett > 0) {
		const stufe = Math.min(Math.round(skelett), 7);
		const soll = steinSollFlaeche(SKELETT_ANTEIL[stufe] / 100, flaeche);
		const radius = steinRadius(stufe, fein, soll);
		steinLage = el('g', { 'clip-path': `url(#${clipId})` });
		steinLage.appendChild(steine(soll, () => steinForm(x, y, b, h, radius, r)));
	}

	if (z.wechselschichtung && !schonGebaendert) {
		let cy = y + 3;
		while (cy < y + h) {
			inner.appendChild(el('line', {
				x1: x, y1: cy, x2: x + b, y2: cy, stroke: '#3c3124', 'stroke-width': 0.6, opacity: 0.22,
			}));
			cy += 4 + r() * 5;
		}
	}
	g.appendChild(inner);
	return steinLage;
}

/** Umriss und Art eines Steins im Bild. */
export interface SteinForm {
	cx: number;
	cy: number;
	/** Ungefaehrer Halbmesser in Pixel. */
	gr: number;
	punkte: [number, number][];
	/** Einer der dunkleren Steine (zwei Grautoene, damit es nicht gestempelt aussieht). */
	dunkel: boolean;
}

/** Farben der Steine: Koerper, Lichtkante, Schatten. */
export const STEIN_FARBEN = {
	hell: '#8a8378',
	dunkel: '#6e675c',
	licht: '#f7f3ea',
	schatten: '#1d1915',
} as const;

/**
 * Die Steine einer Schicht, mit Lichtkante und Schatten.
 *
 * Nutzer-Befund 21.09.2026: In dunklen Schichten wirkten die Steine weniger
 * bedeutend als in hellen. Ein grauer Stein hebt sich von hellem Grund als
 * dunkel ab und von dunklem als hell - dazwischen verschwindet er, und halb
 * durchscheinend, wie er war, nahm er obendrein die Farbe der Schicht an.
 *
 * Jetzt traegt jeder Stein beides (Nutzer-Vorschlag): oben links eine helle
 * Kante, unten rechts einen dunklen Schatten. Auf hellem Grund faellt der
 * Schatten auf, auf dunklem die Kante - so ist ein Stein ueberall gleich gut
 * zu sehen und wirkt dazu plastisch. Der Stein selbst ist dafuer DECKEND:
 * Sonst schimmerten Kante und Schatten durch ihn hindurch.
 *
 * Alle Steine einer Schicht stehen in wenigen Pfaden (je einer fuer Schatten,
 * Kante, die zwei Grautoene und den Glanz) statt in bis zu dreitausend
 * Elementen. Das haelt das Bild schlank, und wo sich zwei Steine
 * ueberlappen, dunkelt ihr Schatten nicht doppelt.
 *
 * Gezeichnet wird, bis die Steine zusammen `sollFlaeche` (Pixel) bedecken,
 * gezaehlt mit `sichtbareSteinflaeche` - hoechstens aber `hoechstens` Steine.
 */
export function steine(
	sollFlaeche: number, form: () => SteinForm, hoechstens = STEINE_HOECHSTENS,
): SVGElement {
	const schatten: string[] = [];
	const licht: string[] = [];
	const hell: string[] = [];
	const dunkel: string[] = [];
	const glanz: string[] = [];
	let belegt = 0;
	for (let i = 0; i < hoechstens && belegt < sollFlaeche; i++) {
		const s = form();
		belegt += sichtbareSteinflaeche(s);
		const versatz = steinRand(s.gr);
		schatten.push(umriss(s.punkte, versatz));
		licht.push(umriss(s.punkte, -versatz));
		(s.dunkel ? dunkel : hell).push(umriss(s.punkte, 0));
		// Ein heller Bogen auf der Oberseite: Erst damit sieht ein Kiesel
		// nach Stein aus und nicht nach grauem Fleck.
		if (s.gr > 1.8) {
			glanz.push(`M${(s.cx - s.gr * 0.55).toFixed(1)} ${(s.cy - s.gr * 0.2).toFixed(1)}`
				+ `Q${s.cx.toFixed(1)} ${(s.cy - s.gr * 0.85).toFixed(1)} `
				+ `${(s.cx + s.gr * 0.5).toFixed(1)} ${(s.cy - s.gr * 0.3).toFixed(1)}`);
		}
	}
	const g = el('g', { class: 'gb-steine' });
	const pfad = (teile: string[], attr: Record<string, string | number>) => {
		if (teile.length) g.appendChild(el('path', { d: teile.join(''), ...attr }));
	};
	const rand = { stroke: '#4f4a42', 'stroke-width': 0.35, 'stroke-opacity': 0.5 };
	pfad(schatten, { fill: STEIN_FARBEN.schatten, opacity: 0.5, class: 'gb-stein-schatten' });
	pfad(licht, { fill: STEIN_FARBEN.licht, opacity: 0.6, class: 'gb-stein-licht' });
	pfad(hell, { fill: STEIN_FARBEN.hell, ...rand, class: 'gb-stein' });
	pfad(dunkel, { fill: STEIN_FARBEN.dunkel, ...rand, class: 'gb-stein' });
	pfad(glanz, { fill: 'none', stroke: '#f0ece4', 'stroke-width': 0.6, opacity: 0.4 });
	return g;
}

/** So viele Steine werden je Abschnitt hoechstens gezeichnet. */
export const STEINE_HOECHSTENS = 3000;

/**
 * Ein Stein: ein unregelmaessiges Vieleck um einen zufaelligen Punkt der
 * Schicht, `radius` ist sein mittlerer Halbmesser in Pixel.
 */
export function steinForm(
	x: number, y: number, b: number, h: number, radius: number, r: () => number,
): SteinForm {
	const cx = x + r() * b, cy = y + r() * h;
	const ecken = 5 + Math.floor(r() * 3);
	const gr = radius * (0.7 + r() * 0.7);
	const punkte: [number, number][] = [];
	for (let k = 0; k < ecken; k++) {
		const w = (k / ecken) * Math.PI * 2;
		const rad = gr * (0.68 + r() * 0.5);
		punkte.push([cx + Math.cos(w) * rad, cy + Math.sin(w) * rad * 0.8]);
	}
	return { cx, cy, gr, punkte, dunkel: r() <= 0.45 };
}

/**
 * Wie gross die Steine einer Schicht werden, als mittlerer Halbmesser in
 * Pixel.
 *
 * Gewoehnlich waechst die Groesse leicht mit der Stufe (`1,4 + 0,22 x
 * Stufe`, bei kleinem Maszstab etwas feiner). Reichen die erlaubten Steine
 * damit nicht, um die Stufe zu belegen, werden sie groesser - genau so viel,
 * dass `STEINE_HOECHSTENS` genuegen (mit Luft: 80 %). Das trifft die hohen
 * Stufen in maechtigen Schichten (Nutzer-Entscheidung 22.09.2026: „Sk6 und
 * Sk7 gern mit groesseren Steinen loesen"): Gemessen zeigte eine 1 m
 * maechtige Sk7-Schicht vorher rund 51 % statt 87,5 % Steinflaeche.
 * In duennen Schichten und bei niedrigen Stufen bleibt es bei der
 * Grundgroesse.
 */
export function steinRadius(stufe: number, fein: number, sollFlaeche: number): number {
	const grund = (1.4 + stufe * 0.22) * Math.max(0.7, fein);
	const { a, b } = steinFlaechenMass();
	// Mittlere Flaeche je Stein: a r² + b r (Stein und Kante/Schatten).
	const jeStein = sollFlaeche / (STEINE_HOECHSTENS * 0.8);
	const noetig = (-b + Math.sqrt(b * b + 4 * a * jeStein)) / (2 * a);
	return Math.max(grund, noetig);
}

let flaechenMass: { a: number; b: number } | null = null;

/**
 * Wie die mittlere sichtbare Flaeche eines Steins mit seinem Halbmesser
 * waechst: `a r² + b r`. Der Stein selbst waechst mit dem Quadrat, sein Rand
 * teils linear (`steinRand`). Ausgezaehlt an Probesteinen derselben Bauart
 * statt von Hand hergeleitet - so stimmt es auch, wenn sich die Form einmal
 * aendert. Dieselben Probesteine in zwei Groessen ergeben beide Beiwerte.
 */
function steinFlaechenMass(): { a: number; b: number } {
	if (!flaechenMass) {
		const mittel = (radius: number) => {
			const r = streu(20260922);
			let summe = 0;
			for (let i = 0; i < 400; i++) summe += sichtbareSteinflaeche(steinForm(0, 0, 100, 100, radius, r));
			return summe / 400;
		};
		const f1 = mittel(1), f2 = mittel(2);
		const a = (f2 - 2 * f1) / 2;
		flaechenMass = { a, b: f1 - a };
	}
	return flaechenMass;
}

/** Wie weit Kante und Schatten ueber den Stein hinausstehen, in Pixel. */
function steinRand(gr: number): number {
	// Groessere Steine werfen einen etwas breiteren Rand.
	return 0.4 + gr * 0.1;
}

/**
 * Wie viel Flaeche die Steine einer Schicht zusammen bedecken sollen, in
 * Pixel.
 *
 * Der Skelettgehalt ist ein VOLUMENanteil (SEA 95 Teil B, Darst. B 3-15). In
 * einer Schnittflaeche wie der Grubenwand ist der Flaechenanteil im Mittel
 * derselbe - gezeichnet wird also `anteil` der Wandflaeche.
 *
 * Die Steine liegen zufaellig und ueberlappen sich; was doppelt belegt ist,
 * sieht man nur einmal. Damit am Ende wirklich `anteil` bedeckt ist, muessen
 * sie zusammen mehr Flaeche mitbringen: bei zufaelliger Lage -ln(1 - anteil).
 * Bei wenigen Steinen macht das kaum etwas aus (Sk4: 19 statt 17,5 %), bei
 * vielen sehr viel. Nach oben gekappt, sonst liefe es bei 100 % gegen
 * unendlich.
 */
export function steinSollFlaeche(anteil: number, flaeche: number): number {
	if (!(anteil > 0)) return 0;
	return -Math.log(1 - Math.min(anteil, 0.95)) * flaeche;
}

/**
 * Die Flaeche, die ein Stein im Bild belegt, in Pixel: der Stein samt heller
 * Kante und Schatten.
 *
 * Nutzer-Befund 21.09.2026: Seit Kante und Schatten dazukamen, wirkten die
 * Steine zu kraeftig. Nachgemessen (Pixel gezaehlt): Der Stein allein traf die
 * Volumenprozente, mit Kante und Schatten belegte er rund 40 % mehr Flaeche.
 * Deshalb zaehlt jetzt der ganze sichtbare Stein.
 *
 * Kante und Schatten sind derselbe Umriss, um `v` nach oben links und nach
 * unten rechts geschoben. Zusammen fuegen sie dem Stein `2 v` mal seine
 * Breite quer zur Schieberichtung hinzu.
 */
export function sichtbareSteinflaeche(s: SteinForm): number {
	let flaeche = 0;
	let min = Infinity, max = -Infinity;
	s.punkte.forEach(([x, y], k) => {
		const [x2, y2] = s.punkte[(k + 1) % s.punkte.length];
		flaeche += x * y2 - x2 * y;
		min = Math.min(min, x - y);
		max = Math.max(max, x - y);
	});
	return Math.abs(flaeche) / 2 + 2 * steinRand(s.gr) * (max - min);
}

/** Ein geschlossener Umriss, schraeg um `versatz` Pixel verschoben. */
function umriss(punkte: [number, number][], versatz: number): string {
	return punkte.map(([px, py], k) =>
		(k ? 'L' : 'M') + (px + versatz).toFixed(1) + ' ' + (py + versatz).toFixed(1)).join('') + 'Z';
}

/* --- Der Text beim Zeigen --------------------------------------------------- */

/**
 * Was beim Zeigen erscheint: die SUBSTRATSCHICHT unter dem Zeiger.
 *
 * Der Horizont steht nicht mehr darin - ihn waehlt der Klick, und er fuellt
 * damit die Tafel neben dem Bild (Nutzer-Entscheidung 19.09.2026). Beides im
 * selben Feld war zu viel auf einmal.
 *
 * `lage` sagt, wo die Schicht in ihrer Wechselkette steht - der Haken einer
 * Zeile gilt fuer sie UND fuer die Schicht darunter, und die traegt ihn selbst
 * nicht (Nutzer-Befund 20.09.2026).
 */
export function substratTipp(a: Abschnitt, lage: KettenLage, versatz = 0): string {
	const zeilen: string[] = [];
	const z = a.substrat;
	// `versatz`: bei einem Filz zaehlen die Tiefen ab Oberkante Ol (filz-tiefen.ts).
	const von = a.von + versatz, bis = a.bis + versatz;
	if (z) {
		zeilen.push(`${von} bis ${bis} cm`);
		zeilen.push('');
		zeilen.push(bodenartText(z.substrat));
		const humus = HUMUS_STUFEN.find(s => s.wert === z.humusgehalt);
		if (humus && zahl(z.humusgehalt) > 0) zeilen.push(`h${z.humusgehalt}: ${humus.bezeichnung}`);
		const skelett = SKELETT_STUFEN.find(s => s.wert === z.skelettgehalt);
		if (skelett && zahl(z.skelettgehalt) > 0) zeilen.push(`Sk${z.skelettgehalt}: ${skelett.bezeichnung}`);
		const karbonat = KARBONAT_STUFEN.find(s => s.wert === z.karbonatgehalt);
		if (karbonat && zahl(z.karbonatgehalt) > 0) zeilen.push(`c${z.karbonatgehalt}: ${karbonat.bezeichnung}`);
		const ld = LAGERUNGSDICHTE_STUFEN.find(s => s.wert === z.lagerungsdichte);
		if (ld) zeilen.push(`Ld${z.lagerungsdichte}: Lagerungsdichte ${ld.name}`);
		// Ein Haken in der LETZTEN Zeile verbindet mit nichts; dort bleibt es
		// beim alten Satz, damit die unvollstaendige Angabe nicht verschwindet.
		if (lage === 'mitte') zeilen.push('im Wechsel mit den Schichten darüber und darunter');
		else if (lage === 'ende') zeilen.push('im Wechsel mit der Schicht darüber');
		else if (lage === 'anfang' || z.wechselschichtung) zeilen.push('im Wechsel mit der Schicht darunter');
	} else {
		zeilen.push(`${von} bis ${bis} cm`, '', 'keine Substratzeile erfasst');
	}
	return zeilen.join('\n');
}
